import { ref, reactive, computed, watch, nextTick } from 'vue';
import { useSettingsApi } from '~/composables/hr-evaluation/conf/settings/useSettingsApi';
import { useHrEvaluationStore } from '~/stores/modules/useHrEvaluationStore';
import { useCommonStore } from '~/stores/useCommonStore';
import { useLoadingStore } from '~/composables/useLoadingStore';
import { settingsT } from '~/composables/hr-evaluation/conf/settings/useHrEvalSettingsI18n';
import { showConfirm, showAlert, showConfirmDialog, confirmAsync } from '~/composables/useConfirmDialog';
import { useHrEvaluationSharedFilter } from '~/composables/hr-evaluation/shared/useHrEvaluationSharedFilter';
import { getEvalSettingsTemplateDef, getEvalSettingsTemplateEvalCycleNm } from '~/composables/hr-evaluation/conf/settings/useEvalSettingsTemplateCatalog';
import {
  buildEvalGradeSettingConflictContext,
  hasAnyGradeLevelConflict,
  resolveGradeConflictPreferAdjust,
  resolveGradeConflictPreferLevelYn,
} from '~/composables/hr-evaluation/conf/settings/useEvalGradeSettingConflict';
import {
  appendLegacyDeptAjaxFilterParams,
  appendLegacyMultiSelectParam,
} from '~/composables/hr-evaluation/conf/settings/useHreConfFilterParams';

// 전역 상태 변수들
const evalGbnDataList = ref<{ id: string; label: string }[]>([]); //평가차수 목록
const findEvalCycleCd = ref<string | null>(null); //평가차수
const findCurrentStep = ref<string | null>(null); //현재스텝

const isEditMode = ref(false); // 평가차수편집 모드

// 인사평가 Wizard 모달 표시 여부
const isInsaWizardOpen = ref(false);

// 불러오기 모달 표시 여부 (store에서 제어하여 Wizard에서도 열 수 있음)
const isLoadSettingsModalOpen = ref(false);

// 설정 요약 모달 표시 여부 (Wizard 완료 후에도 열 수 있음)
const isSettingsSummaryModalOpen = ref(false);

/** 불러오기/템플릿 적용 시 설정 요약 모달에서 사용자 확인 대기 중 */
const isPendingLoadApplySummary = ref(false);

type LoadApplyDeferred = { resolve: (v: unknown) => void; reject: (e?: unknown) => void };
let loadApplyDeferred: LoadApplyDeferred | null = null;
let pendingCopyEvalCycleCd: string | null = null;

// 평가설정 저장 중 로딩 상태
const isSavingEvalSetData = ref(false);

// 인사평가설정 저장 필요 여부
const isEvalSettingsDirty = ref(false);
/** 최초 마운트·로드 중 watch 억제 (1 = bootstrap pause) */
let evalSettingsDirtyWatchPauseDepth = 1;
const isEvalSettingsDirtyWatchPaused = ref(true);
let isEvalSettingsDirtyWatchInitialized = false;

// 각 스텝의 원본 데이터를 저장할 스냅샷
const stepSnapshots = ref<Record<string, any>>({});

/** Step09 평가공개 — 상세팝업 설정 블록 접기/펼치기 */
const isDetailedPopupSettingsOpen = ref(true);
const toggleDetailedPopupSettings = () => {
  isDetailedPopupSettingsOpen.value = !isDetailedPopupSettingsOpen.value;
};

// 인사평가 Wizard 단계 및 cycle 단계 데이터
const wizardStep = ref<
  'main'
  | 'cycle'
  | 'perform'
  | 'ability'
  | 'level'
  | 'multiphase'
  | 'period'
  | 'capa'
  | 'grp'
  | 'perfmdl'
  | 'insa'
  | 'result'
>('main');
const wizardEvalCycleNm = ref(settingsT('wizard.defaultEvalCycleNm'));
const wizardData = reactive({
  performExecYn: false,
  abilityExecYn: false,
});
const wizardEvalCycleCreated = ref(false);
const wizardEvalTargetCreated = ref(false);
const wizardGrpUserAssigned = ref(false);
/** Wizard 진입 경로: 신규 생성(main) | 템플릿 적용 후(cycle) */
const wizardEntryMode = ref<'new' | 'template'>('new');
/** 템플릿 적용 후 cycle 단계 — 부서장 미지정 조직 수 */
const wizardNoManagerDeptCount = ref(0);
/** Wizard에서 부서장 설정하기 클릭 시 index에서 모달 오픈 */
const isDeptManagerModalRequested = ref(false);

/** createGrpUser 성공 시 settings/index에서 Step07 평가군 현황 건수(selectTargetStatusList) 재조회 트리거 */
const step07EvalGrpCountsReloadTick = ref(0);

/** Step07 평가군별 대상자 배정 모달 — 선택된 평가군 ID */
const grpUserAssignSelectedIds = ref<string[]>([]);

const evalBaseData = reactive({
  // 역량역량군 목록
  abilityCmptGrpList: [],

  // 평가항목그룹 목록
  evalItemGrpList: [],

  // 역량 결과 조회 관련 데이터
  abilityResultViewList: [] as string[],
  abilityResultViewGbn: '',
  abilityResultViewItem: '',

  // 대상 관리 데이터
  targetMngData: {
    // 부서장 직접 평가대상자 선정 여부
    managerTargetConfigYn: 'N',

    // 평가대상자 설정기간 시작일
    startDt: '',

    // 평가대상자 설정기간 종료일
    endDt: '',

    // 평가대상자 설정 가이드 내용
    guideContents: '',

    // Step07 성과평가 가이드 (DB EVAL_GUIDE_CONTENTS / saveTargetMngData evalGuideContents)
    evalGuideContents: '',

    // Step07 역량평가 가이드 (DB EVAL_GUIDE_CONTENTS2 / saveTargetMngData evalGuideContents2)
    evalGuideContents2: '',

    // Step08 인사평가 평가화면 옵션 (JSP targetMngData)
    searchGrpYn: 'N',
    evalCapaWeightYn: 'N',
    evalAvgScoreViewYn: 'N',
    evalScoreSimuYn: 'N',
    evalResultSimuYn: 'N',
    evalTargetListExpandYn: 'N',
    evalTargetUserIdShowYn: 'N',

    // ===================== Wizard(result 단계) : 평가결과 공개 설정 =====================
    // 평가결과 조회기간 시작/종료일
    viewStartDt: '',
    viewEndDt: '',

    // 평가결과(매니저) 평가군별 조회 여부
    evalResultGrpYn: 'N',

    // 평가 등급/점수 공개 여부
    evalGradeScoreViewYn: 'N',

    // 성과평가 결과/과정 평가결과 조회 여부
    performResultProcessYn: 'N',

    // 평가결과 상세 팝업 조회 여부
    evalDetailPopYn: 'N',

    // 종합평가의견 피드백 조회 구분
    seniorFeedbackViewYn: 'N',
    mateJnrFeedbackViewYn: 'N',

    // 역량평가 최종결과 조회 내용/옵션
    abilityGbnViewYn: 'N',
    abilitySelfScoreViewYn: 'N',
    abilityTotAvgScoreViewYn: 'N',
    abilityGrpAvgScoreViewYn: 'N',
    abilityChartScoreViewYn: 'N',

    /** 내평가결과 점수 소수점 자릿수 (0~5) */
    evalResultScoreScale: 2,
    /** 내평가결과 점수 소수점 처리 (floor/ceil/round) */
    evalResultRoundGbn: 'round',
  },

  // 기본 설정 데이터
  baseData: {
    // 사용자정의차수 여부 (기본값: true, 2024.08.12 기준)
    evalDegreeYn: true,

    // 성과평가 방법 코드
    performEvalMethodCd: '',

    // 성과상대비교 여부
    performRelativeYn: 'N',

    // 성과 자기평가 여부
    performSelfEvalYn: false,

    // 성과 동료평가 여부
    performMateEvalYn: false,

    // 성과 부하평가 여부
    performSbrdEvalYn: false,

    // 성과평가 차수 코드
    performEvalCycleCd: '',

    // 역량평가 방법 코드
    abilityEvalMethodCd: '',

    // 역량 자기평가 여부
    abilitySelfEvalYn: false,

    // 역량 동료평가 여부
    abilityMateEvalYn: false,

    // 역량 부하평가 여부
    abilitySbrdEvalYn: false,

    // 역량평가 차수 코드
    abilityEvalCycleCd: '',

    // 상급자평가 코드
    seniorEvalCd: 'fir',

    // 상급자평가 방법 코드
    seniorEvalMethodCd: '',

    // 동료평가 포기 여부
    mateEvalGiveupYn: '',

    // 부하평가 포기 여부
    jnrEvalGiveupYn: '',

    // 성과 등급그룹 레벨 여부
    perfGradeGrpLevelYn: false,

    // 역량 등급그룹 레벨 여부
    cmptGradeGrpLevelYn: false,

    // 성과 등급점수 표시 여부 (기본값: true)
    performGradeScoreShowYn: true,

    // 역량 등급점수 표시 여부 (기본값: true)
    abilityGradeScoreShowYn: true,

    // 성과 등급그룹 ID
    perfGradeGrpId: '',

    // 역량 등급그룹 ID
    cmptGradeGrpId: '',

    // 역량역량그룹 ID
    abilityCmptGrpId: '',

    // 성과평가 실행 여부
    performExecYn: false,

    // 역량평가 실행 여부
    abilityExecYn: false,

    // 척도계산방식 여부
    abilityGradeScaleCalcYn: false,
    // 척도계산방식 값
    abilityGradeScaleRate: '',

    // 성과 최대평가점수 여부
    performMaxEvalScoreYn: false,
    // 성과 최대평가점수
    performMaxEvalScore: '',

    // 역량 최대평가점수 여부
    abilityMaxEvalScoreYn: false,
    // 역량 최대평가점수
    abilityMaxEvalScore: '',

    // 성과 평가의견 작성 여부
    performFeedbackWrtngYn: false,

    // 역량 평가의견 작성 여부
    abilityFeedbackWrtngYn: false,

    // 성과 종합평가의견 필수 제외 여부
    performEvalFeedbackRequiredYn: false,

    // 역량 종합평가의견 필수 제외 여부
    abilityEvalFeedbackRequiredYn: false,

    // 2차 등급 조정 범위
    secEvalGradeLimitCd: '',

    // 3차 등급 조정 범위
    thrEvalGradeLimitCd: '',
  },

  // 단위 데이터
  unitData: {
    // 성과평가 1차 단위 코드
    performEvalUnitCd1: '001',

    // 성과평가 2차 단위 코드
    performEvalUnitCd2: '',

    // 역량평가 1차 단위 코드
    abilityEvalUnitCd1: '001',

    // 역량평가 2차 단위 코드
    abilityEvalUnitCd2: '',

    // 평가항목그룹 여부 1
    evalItemGrpYn1: false,

    // 평가항목그룹 여부 2
    evalItemGrpYn2: false,

    // 항목별 평가 시 사용할 평가항목그룹 ID (UI 선택값)
    evalItemGrpId1: '',
    evalItemGrpId2: '',

    // 역량 이전 평가 결과 조회 여부
    abilityBeforeEvalShowYn: false,
  },
});

const evalData = reactive({
  // 평가 목록 데이터
  evalList: [],

  // 역량 구분 데이터
  capaGbnList: [],

  // 평가 그룹 데이터
  evalGrpList: [],

  //삭제 평가군 목록
  evalGrpDeleteList: [],

  // 성과 템플릿 목록
  performTmpltList: [],

  // 인사 설정 데이터
  insaSettingData: [],

  // KR 사이클 목록
  krCycleList: [],

  // 인사 복사 데이터
  insaCopyData: [],

  // 평가 마감 여부
  insaEvalEnd: 'N',

  // 평가 등급 그룹 목록
  evalGradeGrpList: [],
});

/** Step06/07 표시용 — 서버에 저장된 평가군 (Step04 편집 중 evalGrpList와 분리) */
const evalGrpListSaved = ref<any[]>([]);

const mapEvalGrpListFromServer = (list: any[]) =>
  (list || []).map((item: any) => ({
    ...item,
    abilityEvalYn: item.abilityEvalYn === 'Y',
    processEvalYn: item.processEvalYn === 'Y',
    performEvalYn: item.performEvalYn === 'Y',
    resultEvalYn: item.resultEvalYn === 'Y',
    kpiAchieveYn: item.kpiAchieveYn === 'Y',
    oriEvalGrpNm: item.evalGrpNm, // 원본 평가군명을 복사하여 추가
  }));

const syncEvalGrpListSaved = (rawList: any[] | undefined | null) => {
  if (!rawList) return;
  evalGrpListSaved.value = JSON.parse(JSON.stringify(mapEvalGrpListFromServer(rawList)));
};

/** 성과평가 데이터 생성 모달 — 선택된 KR 사이클 ID 목록 */
const selectedKrCycles = ref<string[]>([]);

const evalTargetSelectionData = reactive({
  evalBase: {},
  deptDatas: [],
  userDatas: [],
  posDatas: [],
  jikgubDatas: [],
  jobDatas: [],
  // targetGbnDatas: JSON.parse('${codeUtil:getCodeJsons("406")}'),
  hisMonDatas: [],

  userListAll: [], // 전체 5000개
  loadedCount: 300, // 한 번에 로딩할 수
  observer: null,

  userList: [],
  userCnt: {},
  /** Step05 상단 요약 카드용 — 검색필터와 무관한 전체 평가대상자 집계 */
  summaryUserCnt: {},
  summaryUserListAll: [],
  selectedUserIndex: null,
  makeYearMon: '',
  insaDeptList: [],
  insaDeptIndex: '',
  evalCycleCd: '',
  config: {
    targetMngData: {},
  },
  findHisMon: '',
  newUserList: [],
  chkNewUserDatas: [],
  newUserAllChk: 'N',

  // 선택된 기준년월
  selectedYearMon: '',

  // 초기화 상태 관리
  isInitialized: false,
});

// 설정 요약 데이터
const evalSetData = reactive({
  evalGbnData: {},
  performData: {},
  abilityData: {},
  performLevelList: [],
  abilityLevelList: [],
  performGrpList: [],
  abilityGrpList: [],
  targetMngData: {},
});

// 불러오기 설정 데이터
const loadSettingsData = reactive({
  copyYear: '',
  copyEvalCycleCd: '',
  baseYear: '',
  baseEvalCycleCd: '',
  evalCycleList: [] as any[],
  settingGbn: 'copy', // 'copy' | 'temp'
  tempGbn: '001', // '001' | '002' | '003' | '004' | '005'
  /** true: 위자드 등 신규 차수 생성 후 복사 | false: 현재 선택 차수에 복사 */
  copyToNewEvalCycle: false,
});

let isCommonSyncInitialized = false;

export const useSettingsStore = () => {
  const settingsApi = useSettingsApi();
  const { getEvalCycleList } = useHrEvaluationStore();
  const commonStore = useCommonStore();
  const isLoading = useLoadingStore();
  const { syncEvalCycle } = useHrEvaluationSharedFilter();

  const markEvalSettingsDirty = () => {
    if (!isEvalSettingsDirtyWatchPaused.value) {
      isEvalSettingsDirty.value = true;
    }
  };

  const resetEvalSettingsDirty = () => {
    isEvalSettingsDirty.value = false;
  };

  /** 현재 평가설정 상태를 미저장 변경 비교용 baseline으로 저장 */
  const captureEvalSettingsSnapshot = () => {
    stepSnapshots.value = {
      evalBaseData: JSON.stringify(evalBaseData),
      evalData: JSON.stringify(evalData),
    };
  };

  /**
   * 재조회/생성 결과처럼 사용자가 저장할 변경이 아닌 evalData 필드만 baseline에 반영한다.
   * 전체 스냅샷을 다시 찍으면 다른 미저장 변경이 지워지므로 해당 키만 패치한다.
   */
  const patchEvalDataSnapshot = (patch: Record<string, unknown>) => {
    try {
      const parsed = stepSnapshots.value.evalData ? JSON.parse(stepSnapshots.value.evalData) : null;
      if (!parsed) {
        captureEvalSettingsSnapshot();
        return;
      }
      Object.assign(parsed, patch);
      stepSnapshots.value.evalData = JSON.stringify(parsed);
    } catch {
      captureEvalSettingsSnapshot();
    }
  };

  /** 스냅샷 대비 저장 대상 설정이 달라졌는지 (deep watch 플래그와 별개) */
  const hasUnsavedEvalSettings = () => {
    return (
      stepSnapshots.value.evalBaseData !== JSON.stringify(evalBaseData) ||
      stepSnapshots.value.evalData !== JSON.stringify(evalData)
    );
  };

  const syncEvalSettingsDirtyWatchPaused = () => {
    isEvalSettingsDirtyWatchPaused.value = evalSettingsDirtyWatchPauseDepth > 0;
  };

  const pauseEvalSettingsDirtyWatch = () => {
    evalSettingsDirtyWatchPauseDepth += 1;
    syncEvalSettingsDirtyWatchPaused();
  };

  const resumeEvalSettingsDirtyWatch = () => {
    if (evalSettingsDirtyWatchPauseDepth > 0) {
      evalSettingsDirtyWatchPauseDepth -= 1;
    }
    syncEvalSettingsDirtyWatchPaused();
  };

  const setEvalSettingsDirtyWatchPaused = (paused: boolean) => {
    if (paused) {
      pauseEvalSettingsDirtyWatch();
      return;
    }
    evalSettingsDirtyWatchPauseDepth = 0;
    syncEvalSettingsDirtyWatchPaused();
  };

  if (!isEvalSettingsDirtyWatchInitialized) {
    isEvalSettingsDirtyWatchInitialized = true;
    watch(
      () => [
        evalBaseData.baseData,
        evalBaseData.unitData,
        evalBaseData.targetMngData,
        evalBaseData.abilityResultViewList,
        evalBaseData.abilityResultViewGbn,
        evalBaseData.abilityResultViewItem,
        evalData.evalList,
        evalData.capaGbnList,
        evalData.evalGrpList,
      ],
      markEvalSettingsDirty,
      { deep: true },
    );
  }

  if (!isCommonSyncInitialized) {
    isCommonSyncInitialized = true;

    watch(
      () => findEvalCycleCd.value,
      newVal => {
        const next = newVal ?? '';
        if (commonStore.findEvalCycleCd !== next) {
          commonStore.findEvalCycleCd = next;
        }
      },
    );

    watch(
      () => commonStore.findEvalCycleCd,
      newVal => {
        const next = newVal || '';
        if ((findEvalCycleCd.value ?? '') !== next) {
          findEvalCycleCd.value = next;
        }
      },
      { immediate: true },
    );
  }

  const handleYearChange = async (year: string) => {
    try {
      // API 호출하여 evalGbnDatas 조회
      const list = await getEvalCycleList(year);
      if (list && list.length > 0) {
        // evalGbnDatas 설정
        evalGbnDataList.value = list.map((item: { id: string; label: string }) => ({
          id: item.id,
          label: item.label,
        }));
        const selectedEvalCycleCd = syncEvalCycle(list, findEvalCycleCd);
        if (!selectedEvalCycleCd) {
          evalGbnDataList.value = [];
          findEvalCycleCd.value = null;
        } else {
          // 기준년도만 바꿀 때 탭은 갱신되나 본문(evalBaseData 등)은 switchEvalCycle 전까지 이전 값 유지됨 → 차수 변경과 동일하게 로드
          evalTargetSelectionData.isInitialized = false;
          await switchEvalCycle(selectedEvalCycleCd);
        }
      } else {
        evalGbnDataList.value = [];
        findEvalCycleCd.value = null;
        commonStore.findEvalCycleCd = '';
      }
    } catch (error) {
      console.error('평가구분 데이터 조회 실패:', error);
    }
  };
  /**
   * 차수 탭 변경 처리
   */
  const handleEvalCycleChange = async (evalCycleCd: string) => {
    try {
      // 평가차수 변경 시 초기화 상태 리셋
      evalTargetSelectionData.isInitialized = false;

      // 평가차수 변경 시 switchEvalCycle 호출
      findEvalCycleCd.value = evalCycleCd || null;
      commonStore.findEvalCycleCd = evalCycleCd || '';
      const result = await switchEvalCycle(evalCycleCd);

      return result;
    } catch (error) {
      console.error('평가차수 변경 실패:', error);
      throw error;
    }
  };

  /**
   * 성과/역량 평가가 시행(Y)인데 등급·점수 코드가 비어 있거나 'grade'·'score'가 아니면 '등급' 기본값 적용
   */
  const ensureEvalMethodCdDefaults = () => {
    const base = evalBaseData.baseData;
    if (base.performExecYn && base.performEvalMethodCd !== 'grade' && base.performEvalMethodCd !== 'score') {
      base.performEvalMethodCd = 'grade';
    }
    if (base.abilityExecYn && base.abilityEvalMethodCd !== 'grade' && base.abilityEvalMethodCd !== 'score') {
      base.abilityEvalMethodCd = 'grade';
    }
  };

  /**
   * 서버 데이터를 스토어에 저장하는 함수
   * @param responseData 서버 응답 데이터
   */
  const setEvalBaseData = (responseData: any) => {
    if (!responseData) return;

    // baseData 처리
    if (responseData.baseData) {
      const baseData = { ...responseData.baseData };

      // Y/N 문자열을 boolean으로 변환
      baseData.performSelfEvalYn = baseData.performSelfEvalYn === 'Y';
      baseData.performMateEvalYn = baseData.performMateEvalYn === 'Y';
      baseData.performSbrdEvalYn = baseData.performSbrdEvalYn === 'Y';
      baseData.abilitySelfEvalYn = baseData.abilitySelfEvalYn === 'Y';
      baseData.abilityMateEvalYn = baseData.abilityMateEvalYn === 'Y';
      baseData.abilitySbrdEvalYn = baseData.abilitySbrdEvalYn === 'Y';
      baseData.performExecYn = baseData.performExecYn === 'Y';
      baseData.abilityExecYn = baseData.abilityExecYn === 'Y';
      baseData.performGradeScoreShowYn = baseData.performGradeScoreShowYn === 'Y';
      baseData.abilityGradeScoreShowYn = baseData.abilityGradeScoreShowYn === 'Y';
      baseData.abilityGradeScaleCalcYn = baseData.abilityGradeScaleCalcYn === 'Y';
      baseData.perfGradeGrpLevelYn = baseData.perfGradeGrpLevelYn === 'Y';
      baseData.cmptGradeGrpLevelYn = baseData.cmptGradeGrpLevelYn === 'Y';
      baseData.performMaxEvalScoreYn = baseData.performMaxEvalScoreYn === 'Y';
      baseData.abilityMaxEvalScoreYn = baseData.abilityMaxEvalScoreYn === 'Y';
      baseData.performFeedbackWrtngYn = baseData.performFeedbackWrtngYn === 'Y';
      baseData.abilityFeedbackWrtngYn = baseData.abilityFeedbackWrtngYn === 'Y';

      baseData.performEvalFeedbackRequiredYn = baseData.performEvalFeedbackRequiredYn === 'Y';
      baseData.abilityEvalFeedbackRequiredYn = baseData.abilityEvalFeedbackRequiredYn === 'Y';


      // Y/N 문자열 유지
      baseData.mateEvalGiveupYn = baseData.mateEvalGiveupYn === 'Y' ? 'Y' : 'N';
      baseData.jnrEvalGiveupYn = baseData.jnrEvalGiveupYn === 'Y' ? 'Y' : 'N';
      baseData.performRelativeYn = baseData.performRelativeYn === 'Y' ? 'Y' : 'N';

      // evalBaseData 업데이트
      Object.assign(evalBaseData.baseData, baseData);

      // 기본값 보정
      if (!evalBaseData.baseData.seniorEvalCd) {
        evalBaseData.baseData.seniorEvalCd = 'fir';
      }
      ensureEvalMethodCdDefaults();
    }

    // unitData 처리
    if (responseData.unitData) {
      const unitData = { ...responseData.unitData };

      // unitData의 boolean 필드들 변환
      unitData.evalItemGrpYn1 = unitData.evalItemGrpYn1 === 'Y';
      unitData.evalItemGrpYn2 = unitData.evalItemGrpYn2 === 'Y';
      unitData.abilityBeforeEvalShowYn = unitData.abilityBeforeEvalShowYn === 'Y';

      // evalBaseData 업데이트
      Object.assign(evalBaseData.unitData, unitData);

      // 기본값 보정
      if (!evalBaseData.unitData.performEvalUnitCd1) {
        evalBaseData.unitData.performEvalUnitCd1 = '001';
      }
      if (!evalBaseData.unitData.abilityEvalUnitCd1) {
        evalBaseData.unitData.abilityEvalUnitCd1 = '001';
      }
    }

    // 목록 데이터 처리
    if (responseData.abilityCmptGrpList) {
      // value/label 속성 추가 (value=cmptGrpId, label=cmptGrpNm)
      const mappedList = (responseData.abilityCmptGrpList as any[]).map((item: any) => ({
        ...item,
        value: item.cmptGrpId,
        label: item.cmptGrpNm,
      }));
      evalBaseData.abilityCmptGrpList = JSON.parse(JSON.stringify(mappedList));
    }

    if (responseData.evalItemGrpList) {
      // value/label 속성 추가 (value=evalItemGrpId, label=evalItemGrpNm)
      const mappedList = (responseData.evalItemGrpList as any[]).map((item: any) => ({
        ...item,
        value: item.evalItemGrpId,
        label: item.evalItemGrpNm,
      }));
      evalBaseData.evalItemGrpList = JSON.parse(JSON.stringify(mappedList));
    }
  };

  /** 부서장 직접 선정 OFF(N)일 때 Step06 하위 필드(기간·가이드) 비움 */
  const normalizeManagerTargetConfigSubFields = (target: Record<string, any>) => {
    if (target.managerTargetConfigYn === 'Y') return;
    target.startDt = '';
    target.endDt = '';
    target.guideContents = '';
  };

  /** 내평가결과 점수 표시 옵션 기본값 보정 */
  const normalizeEvalResultScoreFormatFields = (target: Record<string, any>) => {
    const scale = parseInt(String(target.evalResultScoreScale ?? '2'), 10);
    target.evalResultScoreScale = Number.isFinite(scale) ? Math.min(5, Math.max(0, scale)) : 2;
    const roundGbn = String(target.evalResultRoundGbn ?? 'round');
    target.evalResultRoundGbn = ['floor', 'ceil', 'round'].includes(roundGbn) ? roundGbn : 'round';
  };

  /**
   * Step8: 내평가결과 소수점 설정 — Step1 평가방법(점수/등급)과 무관하게 항상 표시
   * (산출에서 결과구분을 점수로 둘 수 있으므로)
   */
  const isEvalResultScoreFormatVisible = computed(() => true);

  const evalResultScoreScaleOptions = computed(() =>
    [0, 1, 2, 3, 4, 5].map(n => ({ value: String(n), label: String(n) })),
  );

  const evalResultRoundGbnOptions = computed(() => [
    { value: 'floor', label: settingsT('evalResultRoundFloor') },
    { value: 'ceil', label: settingsT('evalResultRoundCeil') },
    { value: 'round', label: settingsT('evalResultRoundRound') },
  ]);

  const evalResultScoreScaleModel = computed({
    get: () => {
      const scale = parseInt(String(evalBaseData.targetMngData.evalResultScoreScale ?? 2), 10);
      const n = Number.isFinite(scale) ? Math.min(5, Math.max(0, scale)) : 2;
      return String(n);
    },
    set: (v: string | number) => {
      const scale = parseInt(String(v ?? '2'), 10);
      evalBaseData.targetMngData.evalResultScoreScale = Number.isFinite(scale) ? Math.min(5, Math.max(0, scale)) : 2;
    },
  });

  /** 소수점 자릿수 5(최대)일 때 처리방식 선택 비활성 */
  const isEvalResultRoundGbnDisabled = computed(() => evalResultScoreScaleModel.value === '5');

  /**
   * 서버에서 받은 targetMngData를 스토어에 저장하는 함수
   * @param responseData 서버 응답 데이터
   */
  const setTargetMngData = (responseData: any) => {
    if (!responseData) return;

    // targetMngData 처리
    if (responseData.targetMngData) {
      // 기본값을 유지하면서 서버 값만 덮어쓰기
      Object.assign(evalBaseData.targetMngData, responseData.targetMngData);
      normalizeManagerTargetConfigSubFields(evalBaseData.targetMngData as Record<string, any>);
      normalizeEvalResultScoreFormatFields(evalBaseData.targetMngData as Record<string, any>);
    }

    // abilityResultViewData 처리 (응답 없을 때 이전 차수 값 잔존 방지 → 기본 N/미체크)
    evalBaseData.abilityResultViewList = [];
    if (responseData.abilityResultViewData) {
      if (responseData.abilityResultViewData.gradeShowYn === 'Y') {
        evalBaseData.abilityResultViewList.push('grade');
      }
      if (responseData.abilityResultViewData.scoreShowYn === 'Y') {
        evalBaseData.abilityResultViewList.push('score');
      }
    }

    // abilityResultViewGbn 처리
    evalBaseData.abilityResultViewGbn = '';
    if (responseData.abilityResultViewGbn) {
      evalBaseData.abilityResultViewGbn = Array.isArray(responseData.abilityResultViewGbn)
        ? responseData.abilityResultViewGbn.join(',')
        : responseData.abilityResultViewGbn;
    }
    if ((evalBaseData.targetMngData.abilityGbnViewYn || 'N') !== 'Y') {
      evalBaseData.abilityResultViewGbn = '';
    }

    // abilityResultViewItem 처리
    if (responseData.abilityResultViewItem) {
      evalBaseData.abilityResultViewItem = Array.isArray(responseData.abilityResultViewItem)
        ? responseData.abilityResultViewItem.join(',')
        : responseData.abilityResultViewItem;
    }
  };

  /**
   * 서버에서 받은 evalData를 스토어에 저장하는 함수
   * @param responseData 서버 응답 데이터
   */
  const setEvalData = (responseData: any) => {
    if (!responseData) return;

    evalData.evalGrpDeleteList = [];

    // 평가 목록 데이터
    if (responseData.evalList) {
      evalData.evalList = responseData.evalList;
    }

    // 역량 구분 데이터
    if (responseData.capaGbnList) {
      evalData.capaGbnList = responseData.capaGbnList;
    }

    // 평가 그룹 데이터
    if (responseData.evalGrpList) {
      const mappedEvalGrpList = mapEvalGrpListFromServer(responseData.evalGrpList);
      evalData.evalGrpList = JSON.parse(JSON.stringify(mappedEvalGrpList));
      syncEvalGrpListSaved(mappedEvalGrpList);
    }

    // 성과 템플릿 목록
    if (responseData.performTmpltList) {
      evalData.performTmpltList = responseData.performTmpltList;
    }

    // 인사 설정 데이터
    if (responseData.insaSettingData) {
      evalData.insaSettingData = responseData.insaSettingData;
      // 인사 복사 데이터 (깊은 복사)
      evalData.insaCopyData = JSON.parse(JSON.stringify(responseData.insaSettingData));
    }

    // KR 사이클 목록
    if (responseData.krCycleList) {
      evalData.krCycleList = responseData.krCycleList;
    }

    // 평가 마감 여부
    if (responseData.insaEvalEndYn !== undefined) {
      evalData.insaEvalEnd = responseData.insaEvalEndYn === 'Y' ? 'Y' : 'N';
    }

    // 평가 등급 그룹 목록
    if (responseData.evalGradeGrpList) {
      // value/label 속성 추가 (value=gradeGrpId, label=gradeGrpNm)
      const mappedList = (responseData.evalGradeGrpList as any[]).map((item: any) => ({
        ...item,
        value: item.gradeGrpId,
        label: item.gradeGrpNm,
      }));
      evalData.evalGradeGrpList = JSON.parse(JSON.stringify(mappedList));
    }
  };

  /**
   * 평가차수 변경 시 호출되는 함수
   * @param evalCycleCd 평가차수 코드
   */
  const switchEvalCycle = async (evalCycleCd: string) => {
    pauseEvalSettingsDirtyWatch();
    try {
      // 첫 번째 API 호출: selectEvalBaseData
      const baseDataParams = new URLSearchParams();
      baseDataParams.append('findYear', commonStore.findYear);
      baseDataParams.append('evalCycleCd', evalCycleCd);

      const baseDataResponse = await settingsApi.selectEvalBaseData(baseDataParams);

      // 서버 데이터를 스토어에 저장
      setEvalBaseData(baseDataResponse);

      // 두 번째 API 호출: selectEvalData
      const evalDataParams = new URLSearchParams();
      evalDataParams.append('findYear', commonStore.findYear);
      evalDataParams.append('evalCycleCd', evalCycleCd);
      evalDataParams.append('cmptGrpId', ''); // cmptGrpId가 없으면 빈 문자열

      const evalDataResponse = await settingsApi.selectEvalData(evalDataParams);

      // evalData 저장
      setEvalData(evalDataResponse);

      // 세 번째 API 호출: selectTargetMngData
      const targetMngParams = new URLSearchParams();
      targetMngParams.append('findYear', commonStore.findYear);
      targetMngParams.append('evalCycleCd', evalCycleCd);

      const targetMngResponse = await settingsApi.getTargetMngData(targetMngParams);

      // targetMngData 저장
      setTargetMngData(targetMngResponse);

      // 화면 보정 watch가 기본값을 반영한 뒤 baseline을 찍는다
      await nextTick();
      await nextTick();
      captureEvalSettingsSnapshot();
      resetEvalSettingsDirty();
    } catch (error) {
      console.error('평가차수 변경 중 오류 발생:', error);
      throw error;
    } finally {
      resumeEvalSettingsDirtyWatch();
      if (evalSettingsDirtyWatchPauseDepth === 1) {
        resumeEvalSettingsDirtyWatch();
      }
      resetEvalSettingsDirty();
    }
  };

  const handleTabEdit = async ({ tabId, newLabel }: { tabId: string; newLabel: string }) => {
    try {
      // 현재 탭 정보 찾기
      const tab = evalGbnDataList.value.find((item: { id: string }) => item.id === tabId);
      if (!tab) {
        console.error('탭을 찾을 수 없습니다:', tabId);
        return;
      }

      // 서버에 업데이트 요청
      const params = new URLSearchParams();
      params.append('year', commonStore.findYear);
      params.append('findYear', commonStore.findYear);
      params.append('evalCycleCd', tabId);
      params.append('evalCycleNm', newLabel);

      await settingsApi.updateEvalCycleName(params);

      // UI에 반영
      (tab as { label: string }).label = newLabel;
    } catch (error) {
      console.error('탭 편집 실패:', error);
      // 에러 처리 로직 추가 가능
    }
  };

  const handleTabDelete = async (tabId: string): Promise<boolean> => {
    try {
      // 서버에 삭제 요청
      const params = new URLSearchParams();
      params.append('year', commonStore.findYear);
      params.append('evalCycleCd', tabId);

      await settingsApi.deleteEvalCycle(params);

      // UI에서 탭 제거
      evalGbnDataList.value = evalGbnDataList.value.filter(
        (tab: { id: string }) => tab.id !== tabId,
      );

      showAlert(settingsT('targets.done'), settingsT('m_77d444a231'));

      // 삭제된 차수가 현재 선택된 차수라면 남은 첫 번째 차수로 자동 전환
      if (findEvalCycleCd.value === tabId) {
        const first = evalGbnDataList.value[0] as { id: string } | undefined;
        if (first?.id) {
          findEvalCycleCd.value = first.id;
          await switchEvalCycle(first.id);
        } else {
          findEvalCycleCd.value = null;
        }
      }
      return true;
    } catch (error) {
      console.error('탭 삭제 실패:', error);
      return false;
    }
  };

  // ************************ 현재 평가차수 삭제 패널 state/methods ************************
  const isDeletePanelOpen = ref(false);
  const deleteInput = ref('');

  const currentEvalCycleLabel = computed(
    () => evalGbnDataList.value.find((item: { id: string }) => item.id === findEvalCycleCd.value)?.label ?? '',
  );

  const isDeleteInputValid = computed(
    () => deleteInput.value === currentEvalCycleLabel.value && currentEvalCycleLabel.value !== '',
  );

  const toggleDeletePanel = () => {
    isDeletePanelOpen.value = !isDeletePanelOpen.value;
    deleteInput.value = '';
  };

  const closeDeletePanel = () => {
    isDeletePanelOpen.value = false;
    deleteInput.value = '';
  };

  const handleDeleteCurrentCycle = () => {
    if (!isDeleteInputValid.value || !findEvalCycleCd.value) return;
    showConfirmDialog(settingsT('basisCommon.deleteConfirmTitle'), settingsT('m_afb8e44457'), async () => {
      const success = await handleTabDelete(findEvalCycleCd.value as string);
      if (success) closeDeletePanel();
    });
  };

  const handleRowDragEnd = async (evalCycleCds: string) => {
    try {
      // 서버에 순서 변경 요청
      const params = new URLSearchParams();
      params.append('year', commonStore.findYear);
      params.append('findYear', commonStore.findYear);
      params.append('evalCycleCds', evalCycleCds);

      await settingsApi.updateEvalCycleOrder(params);
    } catch (error) {
      console.error('탭 순서 변경 실패:', error);
      // 에러 처리 로직 추가 가능
    }
  };

  /** KR 사이클 멀티셀렉트 옵션 */
  const krCycleSelectOptions = computed(() =>
    (evalData.krCycleList || []).map((item: { cycleId?: string; cycleNm?: string }) => ({
      value: item.cycleId ?? '',
      label: item.cycleNm ?? '',
    })).filter((opt: { value: string }) => opt.value),
  );

  /**
   * krCycleList 기준으로 selectedKrCycles 초기화 (cycleTargetYn === 'Y')
   */
  const handleInitSelectedKrCycles = () => {
    if (!evalData.krCycleList?.length) {
      selectedKrCycles.value = [];
      return;
    }
    const selected: string[] = [];
    evalData.krCycleList.forEach((item: { cycleId?: string; cycleTargetYn?: string }) => {
      if (item.cycleId && item.cycleTargetYn === 'Y') {
        selected.push(item.cycleId);
      }
    });
    selectedKrCycles.value = selected;
  };

  /**
   * 성과평가 데이터 생성 함수
   * 과거 버전의 createInsaSettingData 함수를 Vue 버전으로 변환
   */
  const createInsaSettingData = async (): Promise<boolean> => {
    try {
      if (hasUnsavedEvalSettings()) {
        showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_de48cd203c'), false, null, null, false);
        return false;
      }

      // 평가 마감 여부 확인
      if (evalData.insaEvalEnd === 'Y') {
        const foundItem = evalGbnDataList.value.find(
          (item: { id: string; label: string }) => item.id === findEvalCycleCd.value,
        );
        const cycleNm = foundItem?.label || '';

        showAlert(settingsT('basisCommon.alert'), cycleNm + ' ' + settingsT('m_01b52e3a6b'));
        return false;
      }

      const hasOkrEvalModule = ((evalData as { evalGrpList: any[] }).evalGrpList || []).some((group: any) => {
        if (!group?.performEvalYn) return false;
        const resultOkr = group.resultEvalYn && String(group.resultEvalMdlCd ?? '') === '002';
        const processOkr = group.processEvalYn && String(group.processEvalMdlCd ?? '') === '002';
        return resultOkr || processOkr;
      });
      const cycleIds = hasOkrEvalModule ? (selectedKrCycles.value || []).join(',') : '';

      // OKR 모듈 선택 시에만 KR 사이클 선택이 필요함
      if (hasOkrEvalModule && !cycleIds) {
        showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_1a68a4da4c'), false, null, null, false);
        return false;
      }

      // 확인 대화상자
      const confirmed = await confirmAsync(settingsT('basisCommon.confirm'), settingsT('m_3a124759d4'));
      if (!confirmed) return false;

      // API 호출 파라미터 설정
      const params = new URLSearchParams();
      params.append('findYear', commonStore.findYear);
      params.append('evalCycleCd', findEvalCycleCd.value || '');
      params.append('cycleIds', cycleIds);

      // API 호출
      const response = await settingsApi.saveInsaDataSet(params);

      // 응답 데이터 처리
      if (response.insaSettingData) {
        evalData.insaSettingData = response.insaSettingData;
        evalData.insaCopyData = JSON.parse(JSON.stringify(response.insaSettingData));
        patchEvalDataSnapshot({
          insaSettingData: evalData.insaSettingData,
          insaCopyData: evalData.insaCopyData,
        });

        showAlert(settingsT('basisCommon.alert'), settingsT('m_3a19d3819c'));
        return true;
      }

      return false;
    } catch (error) {
      console.error('성과평가 데이터 생성 실패:', error);
      showAlert(settingsT('basisCommon.alert'), settingsT('m_fe2f5dc82e'));
      return false;
    }
  };

  /**
   * 평가 대상 선택 데이터 초기화 함수
   * 직원 검색옵션은 selectUserData_json.do
   * - step05(findTargetYn false): findYear, evalCycleCd만
   * - step06(findTargetYn true): findTargetGbnCdArray=Y,E 추가
   * @param forceReload 이미 초기화된 경우에도 재조회
   * @param options.findTargetYn step05는 false, step06은 true(기본)
   */
  const initializeEvalTargetSelectionData = async (
    forceReload = false,
    options: { findTargetYn?: boolean } = {},
  ) => {
    try {
      const { findTargetYn = true } = options;

      // 이미 초기화된 경우 스킵 (step05/06 스텝퍼 클릭 시 forceReload로 재조회)
      if (!forceReload && evalTargetSelectionData.isInitialized) {
        return;
      }

      // 데이터 초기화
      evalTargetSelectionData.userDatas = [];
      evalTargetSelectionData.deptDatas = [];
      evalTargetSelectionData.posDatas = [];
      evalTargetSelectionData.jikgubDatas = [];
      evalTargetSelectionData.jobDatas = [];
      evalTargetSelectionData.hisMonDatas = [];
      evalTargetSelectionData.makeYearMon = '';
      evalTargetSelectionData.summaryUserCnt = {};
      evalTargetSelectionData.summaryUserListAll = [];

      const year = commonStore.findYear;
      const evalCycleCd = findEvalCycleCd.value || '';

      // API 호출 파라미터 설정 (일부 API는 evalCycleCd 유무에 따라 응답이 달라서 fallback 고려)
      const baseParams = new URLSearchParams();
      baseParams.append('findYear', year);
      baseParams.append('year', year);

      const cycleParams = new URLSearchParams(baseParams);
      if (evalCycleCd) {
        cycleParams.append('evalCycleCd', evalCycleCd);
      }

      // 직원 검색옵션 — step05는 추가 파라미터 없음, step06은 대상(Y)+예외(E)
      const userParams = new URLSearchParams();
      userParams.append('findYear', year);
      if (evalCycleCd) {
        userParams.append('evalCycleCd', evalCycleCd);
      }
      if (findTargetYn) {
        userParams.append('findTargetGbnCdArray', 'Y,E');
      }

      // 병렬 API 호출 (우선 evalCycleCd 포함 파라미터로 조회)
      let [userResponse, deptResponse, codeResponse, hisMonResponse] = await Promise.all([
        settingsApi.selectUserData(userParams),
        settingsApi.getDeptInsaList(cycleParams),
        settingsApi.getInsaCodeList(cycleParams),
        settingsApi.getHisMonData(cycleParams),
      ]);

      // 응답 데이터 처리
      const userList = userResponse?.userList || userResponse?.list;
      if (userList) {
        evalTargetSelectionData.userDatas = JSON.parse(JSON.stringify(userList));
      }
      if (deptResponse?.list) {
        evalTargetSelectionData.deptDatas = JSON.parse(JSON.stringify(deptResponse.list));
      }
      if (codeResponse) {
        if (codeResponse.posList) {
          evalTargetSelectionData.posDatas = JSON.parse(JSON.stringify(codeResponse.posList));
        }
        if (codeResponse.jikgubList) {
          evalTargetSelectionData.jikgubDatas = JSON.parse(JSON.stringify(codeResponse.jikgubList));
        }
        if (codeResponse.jobList) {
          evalTargetSelectionData.jobDatas = JSON.parse(JSON.stringify(codeResponse.jobList));
        }
      }
      if (hisMonResponse) {
        if (hisMonResponse.hisMonList) {
          evalTargetSelectionData.hisMonDatas = JSON.parse(
            JSON.stringify(hisMonResponse.hisMonList),
          );
        }
        if (hisMonResponse.makeYearMon) {
          evalTargetSelectionData.makeYearMon = hisMonResponse.makeYearMon;
        }
      }

      // fallback: evalCycleCd 포함 조회 결과가 비어있는 경우(서버 구현 차이) year-only로 재조회
      const isAllEmpty =
        (!evalTargetSelectionData.userDatas || evalTargetSelectionData.userDatas.length === 0) &&
        (!evalTargetSelectionData.deptDatas || evalTargetSelectionData.deptDatas.length === 0) &&
        (!evalTargetSelectionData.posDatas || evalTargetSelectionData.posDatas.length === 0) &&
        (!evalTargetSelectionData.jikgubDatas || evalTargetSelectionData.jikgubDatas.length === 0) &&
        (!evalTargetSelectionData.jobDatas || evalTargetSelectionData.jobDatas.length === 0);

      if (isAllEmpty && evalCycleCd) {
        const fallbackUserParams = new URLSearchParams();
        fallbackUserParams.append('findYear', year);
        if (findTargetYn) {
          fallbackUserParams.append('findTargetGbnCdArray', 'Y,E');
        }

        [userResponse, deptResponse, codeResponse] = await Promise.all([
          settingsApi.selectUserData(fallbackUserParams),
          settingsApi.getDeptInsaList(baseParams),
          settingsApi.getInsaCodeList(baseParams),
        ]);

        const fallbackUserList = userResponse?.userList || userResponse?.list;
        if (fallbackUserList) {
          evalTargetSelectionData.userDatas = JSON.parse(JSON.stringify(fallbackUserList));
        }
        if (deptResponse?.list) {
          evalTargetSelectionData.deptDatas = JSON.parse(JSON.stringify(deptResponse.list));
        }
        if (codeResponse) {
          if (codeResponse.posList) {
            evalTargetSelectionData.posDatas = JSON.parse(JSON.stringify(codeResponse.posList));
          }
          if (codeResponse.jikgubList) {
            evalTargetSelectionData.jikgubDatas = JSON.parse(JSON.stringify(codeResponse.jikgubList));
          }
          if (codeResponse.jobList) {
            evalTargetSelectionData.jobDatas = JSON.parse(JSON.stringify(codeResponse.jobList));
          }
        }
      }

      // 초기화 완료 후 전체 요약 + 기본 검색 실행
      await Promise.all([loadEvalTargetSummaryData(), searchUsersWithFilters({})]);

      // 초기화 완료 상태 설정
      evalTargetSelectionData.isInitialized = true;
    } catch (error) {
      console.error('평가 대상 선택 데이터 초기화 실패:', error);
      throw error;
    }
  };

  /**
   * 인사정보 생성 함수
   * 이전 버전의 createInsaData 함수를 Vue 3 Composition API로 변환
   */
  const createInsaData = async (opts?: { onSuccess?: () => void }) => {
    try {
      // 기준년월이 선택되지 않은 경우
      if (!evalTargetSelectionData.selectedYearMon) {
        showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_cddd24cf44'), false, null, null, false);
        return;
      }

      // 확인 메시지
      const selectedOption = evalTargetSelectionData.hisMonDatas.find(
        (item: any) => item.hisMon === evalTargetSelectionData.selectedYearMon,
      );
      let selectedLabel = selectedOption
        ? (selectedOption as any).hisMonNm || evalTargetSelectionData.selectedYearMon
        : evalTargetSelectionData.selectedYearMon;

      selectedLabel = selectedLabel == 'now' ? settingsT('confirm.currentBaseline') : selectedLabel;

      let msg = settingsT('confirm.insaCreateReset', { selectedLabel });
      if (!evalTargetSelectionData.makeYearMon) {
        msg = settingsT('confirm.insaCreateSimple', { selectedLabel });
      }

      const confirmed = await confirmAsync(
        settingsT('basisCommon.confirm'),
        msg,
      );

      if (!confirmed) return;

      // API 호출 파라미터 설정
      const params = new URLSearchParams();
      params.append('findYear', commonStore.findYear);
      params.append('year', commonStore.findYear);
      params.append('evalCycleCd', findEvalCycleCd.value || '');
      params.append('hisMon', evalTargetSelectionData.selectedYearMon);
      params.append('hisMonNm', selectedLabel);

      // 필터 조건 추가
      if (evalTargetSelectionData.deptDatas && evalTargetSelectionData.deptDatas.length > 0) {
        params.append(
          'findDeptIds',
          evalTargetSelectionData.deptDatas.map((dept: any) => dept.deptId).join(','),
        );
      }
      if (evalTargetSelectionData.userDatas && evalTargetSelectionData.userDatas.length > 0) {
        params.append(
          'findUserIds',
          evalTargetSelectionData.userDatas.map((user: any) => user.userId).join(','),
        );
      }
      if (evalTargetSelectionData.jikgubDatas && evalTargetSelectionData.jikgubDatas.length > 0) {
        params.append(
          'findJikgubIds',
          evalTargetSelectionData.jikgubDatas.map((jikgub: any) => jikgub.jikgubId).join(','),
        );
      }
      if (evalTargetSelectionData.posDatas && evalTargetSelectionData.posDatas.length > 0) {
        params.append(
          'findPosIds',
          evalTargetSelectionData.posDatas.map((pos: any) => pos.posId).join(','),
        );
      }

      // API 호출
      const response = await settingsApi.insertInsaData(params);

      // 응답 데이터 처리
      if (response?.makeYearMon) {
        evalTargetSelectionData.makeYearMon = response.makeYearMon;

        // 사용자 목록·전체 요약 다시 조회
        await Promise.all([loadEvalTargetSummaryData(), searchUsersWithFilters({})]);

        // 선택된 기준년월 초기화
        evalTargetSelectionData.selectedYearMon = '';

        showAlert(settingsT('basisCommon.alert'), settingsT('m_1a8a2fa79d'));
        opts?.onSuccess?.();
      }
    } catch (error) {
      console.error('인사정보 생성 실패:', error);
      showAlert(settingsT('basisCommon.alert'), settingsT('m_8be531e949'));
    }
  };

  /**
   * 인사정보 업데이트 함수
   * 이전 버전의 updateInsaData 함수를 Vue 3 Composition API로 변환
   */
  const updateInsaData = async () => {
    try {
      // 기준년월이 선택되지 않은 경우
      if (!evalTargetSelectionData.selectedYearMon) {
        showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_cddd24cf44'), false, null, null, false);
        return;
      }

      // 확인 메시지
      const selectedOption = evalTargetSelectionData.hisMonDatas.find(
        (item: any) => item.hisMon === evalTargetSelectionData.selectedYearMon,
      );
      let selectedLabel = selectedOption
        ? (selectedOption as any).hisMonNm || evalTargetSelectionData.selectedYearMon
        : evalTargetSelectionData.selectedYearMon;

      selectedLabel = selectedLabel == 'now' ? settingsT('confirm.currentBaseline') : selectedLabel;

      const confirmed = await confirmAsync(
        settingsT('basisCommon.confirm'),
        settingsT('confirm.insaUpdate', { selectedLabel }),
      );

      if (!confirmed) return;

      // API 호출 파라미터 설정
      const params = new URLSearchParams();
      params.append('findYear', commonStore.findYear);
      params.append('year', commonStore.findYear);
      params.append('evalCycleCd', findEvalCycleCd.value || '');
      params.append('hisMon', evalTargetSelectionData.selectedYearMon);
      params.append('hisMonNm', selectedLabel);

      // API 호출
      const response = await settingsApi.updateInsaData(params);

      // 응답 데이터 처리
      if (response?.result === 'SUCCESS') {
        // 평가차수 데이터 다시 로드
        await switchEvalCycle(findEvalCycleCd.value || '');

        const hisMonParams = new URLSearchParams();
        hisMonParams.append('findYear', commonStore.findYear);
        hisMonParams.append('year', commonStore.findYear);
        if (findEvalCycleCd.value) {
          hisMonParams.append('evalCycleCd', findEvalCycleCd.value);
        }

        // 사용자 목록·전체 요약과 함께 생성 완료 기준년월을 다시 조회
        const [hisMonResponse] = await Promise.all([
          settingsApi.getHisMonData(hisMonParams),
          loadEvalTargetSummaryData(),
          searchUsersWithFilters({}),
        ]);
        if (hisMonResponse?.makeYearMon) {
          evalTargetSelectionData.makeYearMon = hisMonResponse.makeYearMon;
        }

        showAlert(settingsT('basisCommon.alert'), response.msg || settingsT('m_4169109a96'));
      }
    } catch (error) {
      console.error('인사정보 업데이트 실패:', error);
      showAlert(settingsT('basisCommon.alert'), settingsT('m_d49d186910'));
    }
  };

  /** 신규직원 추가 모달 목록 조회 — 팝업 년월(findHisMon)은 화면 기준년월(selectedYearMon)과 독립 */
  const popAddNewEmp = async (_init = true) => {
    try {
      evalTargetSelectionData.newUserList = [];
      evalTargetSelectionData.chkNewUserDatas = [];
      evalTargetSelectionData.newUserAllChk = 'N';

      const params = new URLSearchParams();
      params.append('year', commonStore.findYear);
      params.append('evalCycleCd', findEvalCycleCd.value || '');
      params.append('hisMon', evalTargetSelectionData.findHisMon || '');

      const res = await settingsApi.selectInsaNewUserList(params);
      evalTargetSelectionData.newUserList = JSON.parse(JSON.stringify(res?.newUserList || []));
    } catch (error) {
      console.error('신규직원 목록 조회 실패:', error);
      throw error;
    }
  };

  const saveNewUserData = async () => {
    try {
      if (!evalTargetSelectionData.chkNewUserDatas.length) {
        showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_ac4c5d8191'), false, null, null, false);
        return;
      }

      const confirmed = await confirmAsync(settingsT('basisCommon.confirm'), settingsT('m_508da85b8e'));
      if (!confirmed) return;

      const params = new URLSearchParams();
      params.append('findYear', commonStore.findYear);
      params.append('year', commonStore.findYear);
      params.append('evalCycleCd', findEvalCycleCd.value || '');
      params.append('hisMon', evalTargetSelectionData.findHisMon || '');

      evalTargetSelectionData.chkNewUserDatas.forEach((userId: string, i: number) => {
        params.append(`hreInsaUserList[${i}].userId`, userId);
      });

      await settingsApi.saveInsaNewUserData(params);

      // 원본 테이블(평가대상자 목록)·전체 요약 즉시 갱신
      await Promise.all([loadEvalTargetSummaryData(), searchUsersWithFilters({})]);

      // 신규직원 모달 데이터 초기화
      evalTargetSelectionData.newUserList = [];
      evalTargetSelectionData.chkNewUserDatas = [];
      evalTargetSelectionData.newUserAllChk = 'N';
    } catch (error) {
      console.error('신규직원 추가 실패:', error);
      throw error;
    }
  };

  const loadInsaDeptList = async () => {
    try {
      const params = new URLSearchParams();
      params.append('year', commonStore.findYear);
      params.append('evalCycleCd', findEvalCycleCd.value || '');

      const response = await settingsApi.selectInsaDeptList(params);
      const list = JSON.parse(JSON.stringify(response?.insaDeptList || []));

      // 구버전 응답 키(userNm/chiefNm 등) 차이를 흡수
      evalTargetSelectionData.insaDeptList = list.map((row: any) => ({
        ...row,
        chiefNm: row.chiefNm || row.userNm || row.chiefUserNm || '',
        upDeptNm: row.upDeptNm || row.upperDeptNm || row.parentDeptNm || '',
        /** 부서장 직원 ID (검색 선택·저장용) */
        chiefUserId: row.chiefUserId ?? row.chiefId ?? '',
      }));
    } catch (error) {
      console.error('부서장 목록 조회 실패:', error);
      throw error;
    }
  };

  /**
   * 부서장 일괄 저장 (부서장 수정 모달)
   * 백엔드 HreConfMngController.saveInsaDeptList → HreConfMngVO.editList 사용
   * (레거시 JSP: editList[i].deptId / chiefId / chiefNm, year, evalCycleCd)
   * @param rowIndices 편집 중인 행 인덱스만 전송 (레거시와 동일하게 편집 행만 저장)
   */
  const saveInsaDeptList = async (rowIndices: number[]): Promise<boolean> => {
    try {
      if (!rowIndices?.length) {
        showAlert(
          settingsT('basisCommon.alert'),
          settingsT('m_47ae44771a'),
        );
        return false;
      }

      const fullList = evalTargetSelectionData.insaDeptList || [];
      const params = new URLSearchParams();
      params.append('year', commonStore.findYear);
      params.append('evalCycleCd', findEvalCycleCd.value || '');

      let listIdx = 0;
      for (const idx of rowIndices) {
        const row = fullList[idx] as Record<string, any> | undefined;
        if (!row) continue;
        params.append(`editList[${listIdx}].deptId`, String(row.deptId ?? ''));
        const chiefId = row.chiefUserId ?? row.chiefId ?? '';
        params.append(`editList[${listIdx}].chiefId`, String(chiefId));
        params.append(
          `editList[${listIdx}].chiefNm`,
          String(row.chiefNm || row.userNm || row.chiefUserNm || ''),
        );
        listIdx++;
      }

      if (listIdx === 0) {
        showAlert(settingsT('basisCommon.alert'), settingsT('m_915d4346cb'));
        return false;
      }

      const response = await settingsApi.saveInsaDeptList(params);

      // 성공 시 jsonView에는 insaDeptList만 담기고 result 키가 없음. 실패 시 result=FAIL.
      if (response?.result === 'FAIL') {
        showAlert(settingsT('basisCommon.error'), response?.msg || settingsT('m_e7a68f3817'));
        return false;
      }

      if (response?.insaDeptList && Array.isArray(response.insaDeptList)) {
        const list = JSON.parse(JSON.stringify(response.insaDeptList));
        evalTargetSelectionData.insaDeptList = list.map((r: any) => ({
          ...r,
          chiefNm: r.chiefNm || r.userNm || r.chiefUserNm || '',
          upDeptNm: r.upDeptNm || r.upperDeptNm || r.parentDeptNm || '',
          chiefUserId: r.chiefUserId ?? r.chiefId ?? '',
        }));
      } else {
        await loadInsaDeptList();
      }

      // Wizard 배너(부서장 없는 조직 수) 및 동일 목록 기반 건수 동기화
      wizardNoManagerDeptCount.value = countDeptsWithoutChief();

      showAlert(settingsT('basisCommon.alert'), settingsT('m_5fad5d34a0'));
      return true;
    } catch (error) {
      console.error('부서장 목록 저장 실패:', error);
      showAlert(settingsT('basisCommon.error'), settingsT('m_a5d5c4aad6'));
      return false;
    }
  };

  /**
   * 대상구분 필터 — 서버는 findTargetGbnCd를 콤마 구분 문자열로 수신 후 IN 절로 처리
   */
  const appendTargetGbnFilterParams = (params: URLSearchParams, targetTypes?: string[]) => {
    params.append('findTargetGbnCd', targetTypes?.length ? targetTypes.join(',') : '');
  };

  /**
   * Step06 평가대상자 편집 저장
   * - 레거시 hreConfMngList.jsp saveUserData와 동일하게 saveUserData_json.do 호출
   * - 삭제는 deleteInsaUserData에서 별도 처리되므로 여기서는 수정(editList)만 전송
   */
  const saveEditedUserData = async (
    editedRows: any[],
    filters: {
      targetTypes?: string[];
      departments?: string[];
      employees?: string[];
      grades?: string[];
      positions?: string[];
      jobs?: string[];
    } = {},
    deletedUserIds: string[] = [],
  ): Promise<boolean> => {
    try {
      const beforeList = evalTargetSelectionData.userList || [];
      const beforeMap = new Map(
        beforeList
          .filter((row: any) => row?.userId != null && row?.userId !== '')
          .map((row: any) => [String(row.userId), row]),
      );

      const normalizeJoinDt = (value: any) => String(value ?? '').replace(/\D/g, '');
      const changedRows = (editedRows || []).filter((row: any) => {
        const userId = String(row?.userId ?? '');
        if (!userId) return false;
        const prev = beforeMap.get(userId);
        if (!prev) return false;

        return (
          String(row.deptId ?? '') !== String(prev.deptId ?? '') ||
          normalizeJoinDt(row.joinDt) !== normalizeJoinDt(prev.joinDt) ||
          String(row.posId ?? '') !== String(prev.posId ?? '') ||
          String(row.jikgubId ?? '') !== String(prev.jikgubId ?? '') ||
          String(row.jobId ?? '') !== String(prev.jobId ?? '') ||
          String(row.targetGbnCd ?? '') !== String(prev.targetGbnCd ?? '')
        );
      });

      const deleteIds = Array.from(
        new Set((deletedUserIds || []).map(v => String(v || '')).filter(v => v !== ''))
      );

      if (!changedRows.length && !deleteIds.length) {
        showAlert(settingsT('basisCommon.alert'), settingsT('basisCommon.noDataToSave'));
        return false;
      }

      if (changedRows.length) {
        const params = new URLSearchParams();
        params.append('year', commonStore.findYear);
        params.append('findYear', commonStore.findYear);
        params.append('evalCycleCd', findEvalCycleCd.value || '');
        appendTargetGbnFilterParams(params, filters.targetTypes);

        appendLegacyDeptAjaxFilterParams(params, filters.departments);
        appendLegacyMultiSelectParam(params, 'findUserIds', filters.employees);
        appendLegacyMultiSelectParam(params, 'findJikgubIds', filters.grades);
        appendLegacyMultiSelectParam(params, 'findPosIds', filters.positions);
        appendLegacyMultiSelectParam(params, 'findJobIds', filters.jobs);

        changedRows.forEach((row: any, idx: number) => {
          params.append(`editList[${idx}].userId`, String(row.userId ?? ''));
          params.append(`editList[${idx}].deptId`, String(row.deptId ?? ''));
          params.append(`editList[${idx}].joinDt`, normalizeJoinDt(row.joinDt));
          params.append(`editList[${idx}].posId`, String(row.posId ?? ''));
          params.append(`editList[${idx}].jikgubId`, String(row.jikgubId ?? ''));
          params.append(`editList[${idx}].jobId`, String(row.jobId ?? ''));
          params.append(`editList[${idx}].targetGbnCd`, String(row.targetGbnCd ?? ''));
        });

        const response = await settingsApi.saveUserData(params);
        if (response?.result === 'FAIL') {
          showAlert(settingsT('basisCommon.error'), response?.msg || settingsT('m_d320c22f5a'));
          return false;
        }
      }

      if (deleteIds.length) {
        const deleteParams = new URLSearchParams();
        deleteIds.forEach((userId, index) => {
          deleteParams.append(`deleteList[${index}].userId`, userId);
        });
        deleteParams.append('year', commonStore.findYear);
        deleteParams.append('evalCycleCd', findEvalCycleCd.value || '');

        const deleteResponse = await settingsApi.deleteInsaUserData(deleteParams);
        if (deleteResponse?.result === 'FAIL') {
          showAlert(settingsT('basisCommon.error'), deleteResponse?.msg || settingsT('m_ba3ff7046c'));
          return false;
        }

        if (Array.isArray(evalTargetSelectionData.userDatas)) {
          evalTargetSelectionData.userDatas = evalTargetSelectionData.userDatas.filter(
            (item: any) => !deleteIds.includes(String(item?.userId ?? '')),
          );
        }
      }

      await Promise.all([loadEvalTargetSummaryData(), searchUsersWithFilters(filters)]);

      showAlert(settingsT('targets.done'), settingsT('m_0c47ff0995'));
      return true;
    } catch (error) {
      console.error('평가대상자 저장 실패:', error);
      showAlert(settingsT('basisCommon.error'), settingsT('m_d320c22f5a'));
      return false;
    }
  };

  /** Step06 평가대상자 삭제 (레거시 deleteInsaUserData → deleteEvalTarget.do) */
  const deleteInsaUserData = async (
    userIds: string[],
    filters: {
      targetTypes?: string[];
      departments?: string[];
      employees?: string[];
      grades?: string[];
      positions?: string[];
      jobs?: string[];
    } = {},
  ): Promise<boolean> => {
    try {
      const ids = (userIds || []).map(v => String(v || '')).filter(v => v !== '');
      if (!ids.length) {
        showAlert(settingsT('basisCommon.alert'), settingsT('m_ac4c5d8191'));
        return false;
      }

      const params = new URLSearchParams();
      ids.forEach((userId, index) => {
        params.append(`deleteList[${index}].userId`, userId);
      });
      params.append('year', commonStore.findYear);
      params.append('evalCycleCd', findEvalCycleCd.value || '');

      const response = await settingsApi.deleteInsaUserData(params);
      if (response?.result === 'FAIL') {
        showAlert(settingsT('basisCommon.error'), response?.msg || settingsT('m_ba3ff7046c'));
        return false;
      }

      await Promise.all([loadEvalTargetSummaryData(), searchUsersWithFilters(filters)]);

      // 직원 필터 데이터셋에서도 제거(레거시 selectize remove와 동일 목적)
      if (Array.isArray(evalTargetSelectionData.userDatas)) {
        evalTargetSelectionData.userDatas = evalTargetSelectionData.userDatas.filter(
          (item: any) => !ids.includes(String(item?.userId ?? '')),
        );
      }

      showAlert(settingsT('targets.done'), settingsT('basisCommon.deleted'));
      return true;
    } catch (error) {
      console.error('평가대상자 삭제 실패:', error);
      showAlert(settingsT('basisCommon.error'), settingsT('m_ba3ff7046c'));
      return false;
    }
  };

  /**
   * Step05 상단 요약 카드용 전체 평가대상자 데이터 조회
   * 검색필터와 분리 — 항상 필터 없이 selectUserData_json.do 호출
   */
  const loadEvalTargetSummaryData = async () => {
    try {
      const params = new URLSearchParams();
      params.append('findYear', commonStore.findYear);
      params.append('evalCycleCd', findEvalCycleCd.value || '');
      params.append('findTargetGbnCd', '');

      const response = await settingsApi.selectUserData(params);

      if (response?.userCnt) {
        evalTargetSelectionData.summaryUserCnt = JSON.parse(JSON.stringify(response.userCnt));
      }
      if (response?.userList) {
        evalTargetSelectionData.summaryUserListAll = JSON.parse(JSON.stringify(response.userList));
      }
    } catch (error) {
      console.error('평가대상자 요약 데이터 로드 실패:', error);
      throw error;
    }
  };

  /**
   * 필터 검색 함수
   * 이전 버전의 필터 검색 로직을 Vue 3 Composition API로 변환
   */
  const searchUsersWithFilters = async (filters: any) => {
    try {
      // 사용자 목록 초기화
      evalTargetSelectionData.userList = [];
      evalTargetSelectionData.userListAll = [];

      // API 호출 파라미터 설정
      const params = new URLSearchParams();
      params.append('findYear', commonStore.findYear);
      params.append('evalCycleCd', findEvalCycleCd.value || '');
      appendTargetGbnFilterParams(params, filters.targetTypes);

      appendLegacyDeptAjaxFilterParams(params, filters.departments);
      appendLegacyMultiSelectParam(params, 'findUserIds', filters.employees);
      appendLegacyMultiSelectParam(params, 'findJikgubIds', filters.grades);
      appendLegacyMultiSelectParam(params, 'findPosIds', filters.positions);
      appendLegacyMultiSelectParam(params, 'findJobIds', filters.jobs);

      // API 호출
      const response = await settingsApi.selectUserData(params);

      // 응답 데이터 처리
      if (response?.userList) {
        evalTargetSelectionData.userListAll = JSON.parse(JSON.stringify(response.userList));
        evalTargetSelectionData.userList = evalTargetSelectionData.userListAll.slice(
          0,
          evalTargetSelectionData.loadedCount,
        );
      }

      if (response?.userCnt) {
        evalTargetSelectionData.userCnt = JSON.parse(JSON.stringify(response.userCnt));
      }
    } catch (error) {
      console.error('필터 검색 실패:', error);
      throw error;
    }
  };

  /**
   * 설정 요약 팝업 열기
   * 목적: 설정 요약 데이터를 조회하고 팝업을 표시
   * @param year 연도
   * @param evalCycleCd 평가차수 코드
   */
  const openEvalSetSummary = async (year: string, evalCycleCd: string) => {
    try {
      const params = new URLSearchParams();
      params.append('findYear', year);
      params.append('findEvalCycleCd', evalCycleCd);

      const response = await settingsApi.selectEvalSetSummary(params);

      // 응답 데이터 처리
      if (response?.evalGbnData) {
        evalSetData.evalGbnData = { ...response.evalGbnData };
        (evalSetData.evalGbnData as any).performExecYn = (evalSetData.evalGbnData as any).performExecYn === 'Y';
        (evalSetData.evalGbnData as any).abilityExecYn = (evalSetData.evalGbnData as any).abilityExecYn === 'Y';
      }

      if (response?.performData) {
        evalSetData.performData = response.performData;
      }

      if (response?.abilityData) {
        evalSetData.abilityData = response.abilityData;
      }

      if (response?.performLevelList) {
        evalSetData.performLevelList = response.performLevelList;
      }

      if (response?.abilityLevelList) {
        evalSetData.abilityLevelList = response.abilityLevelList;
      }

      if (response?.performGrpList) {
        evalSetData.performGrpList = response.performGrpList;
      }

      if (response?.abilityGrpList) {
        evalSetData.abilityGrpList = response.abilityGrpList;
      }

      if (response?.targetMngData) {
        evalSetData.targetMngData = response.targetMngData;
      }

      return true;
    } catch (error) {
      console.error('설정 요약 데이터 조회 실패:', error);
      throw error;
    }
  };

  /**
   * QuickEdit 변경 사항 반영
   * 목적: 예전 onChangeQuickEdit(quickEditType)와 동일하게,
   *       변경된 종류(등급/성과항목/역량항목)에 해당하는 데이터만 부분 갱신
   */
  const onChangeQuickEdit = async (quickEditType: 'grade' | 'performItem' | 'capaItem') => {
    try {
      const params = new URLSearchParams();
      params.append('findYear', commonStore.findYear);
      params.append('quickEditType', quickEditType);

      const response = await settingsApi.selectQuickEditData(params);
      if (!response) return;

      if (quickEditType === 'grade' && response.evalGradeGrpList) {
        // 평가등급 그룹 목록만 갱신 (콤보용 value/label 매핑 포함)
        setEvalData({ evalGradeGrpList: response.evalGradeGrpList });
      } else if (quickEditType === 'performItem' && response.evalItemGrpList) {
        // 성과 평가항목 그룹 목록만 갱신
        setEvalBaseData({ evalItemGrpList: response.evalItemGrpList });
      } else if (quickEditType === 'capaItem' && response.abilityCmptGrpList) {
        // 역량 평가항목 그룹 목록만 갱신
        setEvalBaseData({ abilityCmptGrpList: response.abilityCmptGrpList });
      }
    } catch (error) {
      console.error('onChangeQuickEdit 처리 실패:', error);
      throw error;
    }
  };

  /**
   * 불러오기 모달 열기
   * 목적: 불러오기 버튼 클릭 시 모달 표시
   * @param year 연도
   * @param evalCycleCd 평가차수 코드
   * @param settingGbn 설정 구분 (copy/temp)
   */
  const openLoadSettingsModal = async (
    year: string,
    evalCycleCd: string,
    settingGbn: string = 'copy',
  ) => {
    try {
      loadSettingsData.copyYear = year;
      loadSettingsData.baseYear = year;
      loadSettingsData.baseEvalCycleCd = evalCycleCd;
      loadSettingsData.settingGbn = settingGbn;
      await getLoadSettingsEvalCycleList();
      return true;
    } catch (error) {
      console.error('불러오기 모달 열기 실패:', error);
      throw error;
    }
  };

  /**
   * 불러오기용 연도별 평가차수 목록 조회
   * 목적: 선택한 연도의 평가차수 목록을 조회
   */
  const getLoadSettingsEvalCycleList = async () => {
    try {
      const params = new URLSearchParams();
      params.append('findYear', loadSettingsData.copyYear);

      const response = await settingsApi.selectYearEvalCycleList(params);

      if (response?.evalCycleList) {
        loadSettingsData.evalCycleList = response.evalCycleList;

        // 현재 연도와 동일하면 현재 평가차수 제외 (신규 차수 생성 복사 시에는 전체 목록 표시)
        if (!loadSettingsData.copyToNewEvalCycle && loadSettingsData.baseYear === loadSettingsData.copyYear) {
          loadSettingsData.evalCycleList = loadSettingsData.evalCycleList.filter(
            (item: any) => item.evalCycleCd !== loadSettingsData.baseEvalCycleCd,
          );
        }

        // 목록이 있으면 항상 첫 번째 항목 선택
        if (loadSettingsData.evalCycleList.length > 0) {
          loadSettingsData.copyEvalCycleCd = loadSettingsData.evalCycleList[0].evalCycleCd;
        } else {
          loadSettingsData.copyEvalCycleCd = '';
        }
      } else {
        loadSettingsData.evalCycleList = [];
        loadSettingsData.copyEvalCycleCd = '';
      }
    } catch (error) {
      console.error('평가차수 목록 조회 실패:', error);
      throw error;
    }
  };

  /**
   * 평가차수 설정 복사
   * 목적: 이전 평가차수 설정을 현재 평가차수로 복사
   * @param evalSetCheckYn 설정 요약 확인 여부
   */
  const copyEvalCycleSet = async (evalSetCheckYn: string = 'N', targetEvalCycleCd: string = loadSettingsData.baseEvalCycleCd) => {
    try {
      if (!loadSettingsData.copyEvalCycleCd) {
        throw new Error(settingsT('m_830168a4bb'));
      }

      const params = new URLSearchParams();
      params.append('copyYear', loadSettingsData.copyYear);
      params.append('copyEvalCycleCd', loadSettingsData.copyEvalCycleCd);
      params.append('baseYear', loadSettingsData.baseYear);
      params.append('baseEvalCycleCd', targetEvalCycleCd);
      params.append('evalSetCheckYn', evalSetCheckYn);

      const response = await settingsApi.copyEvalCycleSet(params);

      // 백엔드에서 makeFailJsonData로 반환한 경우 처리
      if (response?.result === 'FAIL') {
        showConfirm('warning', settingsT('basisCommon.notice'), response.msg, false, null, null, false);
        return null;
      }

      return response;
    } catch (error) {
      console.error('평가차수 설정 복사 실패:', error);
      throw error;
    }
  };

  const getCopyEvalCycleNm = () => {
    const selectedEvalCycle = loadSettingsData.evalCycleList.find(
      (item: { evalCycleCd?: string }) => item.evalCycleCd === loadSettingsData.copyEvalCycleCd,
    );
    const baseNm = selectedEvalCycle?.evalCycleNm || settingsT('wizard.defaultEvalCycleNm');
    return `${baseNm} ${settingsT('confirm.copySuffix')}`;
  };

  const createCopiedEvalCycle = async () => {
    const year = loadSettingsData.baseYear || commonStore.findYear;
    if (!year) {
      showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_d84a65db8d'), false, null, null, false);
      return null;
    }

    const params = new URLSearchParams();
    params.append('findYear', year);
    params.append('year', year);
    params.append('evalCycleNm', getCopyEvalCycleNm());

    const response = await settingsApi.saveEvalCycle(params);
    if (response?.evalCycleList?.length) {
      evalGbnDataList.value = response.evalCycleList.map((item: { evalCycleCd: string; evalCycleNm: string }) => ({
        id: item.evalCycleCd,
        label: item.evalCycleNm,
      }));
    }

    return response?.evalCycleCd ?? response?.evalCycleList?.[response.evalCycleList.length - 1]?.evalCycleCd ?? null;
  };

  const cleanupPendingCopyEvalCycle = async () => {
    if (!pendingCopyEvalCycleCd) {
      return;
    }

    const deleteEvalCycleCd = pendingCopyEvalCycleCd;
    pendingCopyEvalCycleCd = null;

    try {
      const params = new URLSearchParams();
      params.append('year', loadSettingsData.baseYear || commonStore.findYear);
      params.append('evalCycleCd', deleteEvalCycleCd);
      await settingsApi.deleteEvalCycle(params);
      evalGbnDataList.value = evalGbnDataList.value.filter((tab: { id: string }) => tab.id !== deleteEvalCycleCd);
    } catch (error) {
      console.error('요약 취소 평가차수 삭제 실패:', error);
    }
  };

  /**
   * 템플릿 평가 설정 저장
   * 목적: 선택한 템플릿으로 평가 설정 저장
   * @param templateTypeCd 템플릿 타입 코드
   * @param evalSetCheckYn 설정 요약 확인 여부
   */
  const saveTemplateEvalSet = async (
    templateTypeCd: string,
    evalSetCheckYn: string = 'N',
    targetEvalCycleCd: string = loadSettingsData.baseEvalCycleCd,
  ) => {
    try {
      const params = new URLSearchParams();
      params.append('baseYear', loadSettingsData.baseYear);
      params.append('baseEvalCycleCd', targetEvalCycleCd);
      params.append('templateTypeCd', templateTypeCd);
      params.append('evalSetCheckYn', evalSetCheckYn);

      const response = await settingsApi.saveTemplateEvalSet(params);

      // 백엔드에서 makeFailJsonData로 반환한 경우 처리
      if (response?.result === 'FAIL') {
        showConfirm('warning', settingsT('basisCommon.notice'), response.msg, false, null, null, false);
        return null;
      }

      return response;
    } catch (error) {
      console.error('템플릿 평가 설정 저장 실패:', error);
      throw error;
    }
  };

  const getTemplateEvalCycleNm = (templateTypeCd: string) => getEvalSettingsTemplateEvalCycleNm(templateTypeCd);

  const createTemplateEvalCycle = async (templateTypeCd: string) => {
    const year = loadSettingsData.baseYear || commonStore.findYear;
    if (!year) {
      showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_d84a65db8d'), false, null, null, false);
      return null;
    }

    const params = new URLSearchParams();
    params.append('findYear', year);
    params.append('year', year);
    params.append('evalCycleNm', getTemplateEvalCycleNm(templateTypeCd));

    const response = await settingsApi.saveEvalCycle(params);
    if (response?.evalCycleList?.length) {
      evalGbnDataList.value = response.evalCycleList.map((item: { evalCycleCd: string; evalCycleNm: string }) => ({
        id: item.evalCycleCd,
        label: item.evalCycleNm,
      }));
    }

    return response?.evalCycleCd ?? response?.evalCycleList?.[response.evalCycleList.length - 1]?.evalCycleCd ?? null;
  };

  const applyEvalSetSummaryResponseToEvalSetData = (summaryResponse: Record<string, unknown>) => {
    if (summaryResponse?.evalGbnData) {
      evalSetData.evalGbnData = { ...(summaryResponse.evalGbnData as object) } as typeof evalSetData.evalGbnData;
      (evalSetData.evalGbnData as any).performExecYn = (evalSetData.evalGbnData as any).performExecYn === 'Y';
      (evalSetData.evalGbnData as any).abilityExecYn = (evalSetData.evalGbnData as any).abilityExecYn === 'Y';
    }

    if (summaryResponse?.performData) {
      evalSetData.performData = summaryResponse.performData as typeof evalSetData.performData;
    }

    if (summaryResponse?.abilityData) {
      evalSetData.abilityData = summaryResponse.abilityData as typeof evalSetData.abilityData;
    }

    if (summaryResponse?.performLevelList) {
      evalSetData.performLevelList = summaryResponse.performLevelList as typeof evalSetData.performLevelList;
    }

    if (summaryResponse?.abilityLevelList) {
      evalSetData.abilityLevelList = summaryResponse.abilityLevelList as typeof evalSetData.abilityLevelList;
    }

    if (summaryResponse?.performGrpList) {
      evalSetData.performGrpList = summaryResponse.performGrpList as typeof evalSetData.performGrpList;
    }

    if (summaryResponse?.abilityGrpList) {
      evalSetData.abilityGrpList = summaryResponse.abilityGrpList as typeof evalSetData.abilityGrpList;
    }

    if (summaryResponse?.targetMngData) {
      evalSetData.targetMngData = summaryResponse.targetMngData as typeof evalSetData.targetMngData;
    }
  };

  /** 1단계: 설정 요약만 조회(evalSetCheckYn=Y, 롤백) 후 evalSetData 반영 */
  const prepareEvalSetSummaryCopy = async () => {
    await cleanupPendingCopyEvalCycle();

    let targetEvalCycleCd: string | null = null;
    if (loadSettingsData.copyToNewEvalCycle) {
      const newEvalCycleCd = await createCopiedEvalCycle();
      if (!newEvalCycleCd) {
        return null;
      }
      pendingCopyEvalCycleCd = newEvalCycleCd;
      targetEvalCycleCd = newEvalCycleCd;
    } else {
      if (!loadSettingsData.baseEvalCycleCd) {
        showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_3c871b2a5d'), false, null, null, false);
        return null;
      }
      targetEvalCycleCd = loadSettingsData.baseEvalCycleCd;
    }
    if (!targetEvalCycleCd) {
      return null;
    }

    const summaryResponse = await copyEvalCycleSet('Y', targetEvalCycleCd);
    if (!summaryResponse) {
      await cleanupPendingCopyEvalCycle();
      return null;
    }
    applyEvalSetSummaryResponseToEvalSetData(summaryResponse as Record<string, unknown>);
    return summaryResponse;
  };

  /** 2단계: 실제 복사 저장 */
  const finalizeEvalSetSummaryCopy = async () => {
    const targetEvalCycleCd = loadSettingsData.copyToNewEvalCycle
      ? pendingCopyEvalCycleCd
      : loadSettingsData.baseEvalCycleCd;
    if (!targetEvalCycleCd) {
      return null;
    }

    const response = await copyEvalCycleSet('N', targetEvalCycleCd);
    if (!response) {
      if (loadSettingsData.copyToNewEvalCycle) {
        await cleanupPendingCopyEvalCycle();
      }
      return null;
    }

    if (loadSettingsData.copyToNewEvalCycle) {
      pendingCopyEvalCycleCd = null;
    }
    return {
      ...response,
      baseEvalCycleCd: response.baseEvalCycleCd ?? targetEvalCycleCd,
      evalCycleCd: response.evalCycleCd ?? targetEvalCycleCd,
    };
  };

  /** 1단계: 템플릿 적용 요약만 조회 */
  const prepareEvalSetSummaryTemp = async (templateTypeCd: string) => {
    const summaryResponse = await saveTemplateEvalSet(templateTypeCd, 'Y');
    if (!summaryResponse) {
      return null;
    }
    applyEvalSetSummaryResponseToEvalSetData(summaryResponse as Record<string, unknown>);
    return summaryResponse;
  };

  /** 2단계: 템플릿 실제 저장 */
  const finalizeEvalSetSummaryTemp = async (templateTypeCd: string) => {
    return await saveTemplateEvalSet(templateTypeCd, 'N');
  };

  /** 템플릿은 현재 차수에 덮어쓰지 않고, 템플릿명 신규 차수를 만든 뒤 해당 차수에 적용 */
  const createEvalCycleAndSaveTemplateEvalSet = async (templateTypeCd: string) => {
    const newEvalCycleCd = await createTemplateEvalCycle(templateTypeCd);
    if (!newEvalCycleCd) {
      return null;
    }

    const response = await saveTemplateEvalSet(templateTypeCd, 'N', newEvalCycleCd);
    if (!response) {
      return null;
    }

    return {
      ...response,
      baseEvalCycleCd: response.baseEvalCycleCd ?? newEvalCycleCd,
      evalCycleCd: response.evalCycleCd ?? newEvalCycleCd,
    };
  };

  /**
   * 설정 요약 확인 후 평가차수 설정 복사
   * 목적: 설정 요약을 확인한 후 평가차수 설정 복사
   * 백엔드에서 evalSetCheckYn='Y'일 때는 설정 요약만 조회하고 롤백하므로,
   * 실제 저장은 evalSetCheckYn='N'으로 다시 호출해야 함
   */
  const confirmEvalSetSummaryCopy = async () => {
    try {
      const prepared = await prepareEvalSetSummaryCopy();
      if (!prepared) {
        return null;
      }
      return await finalizeEvalSetSummaryCopy();
    } catch (error) {
      if (loadSettingsData.copyToNewEvalCycle) {
        await cleanupPendingCopyEvalCycle();
      }
      console.error('설정 요약 확인 후 복사 실패:', error);
      throw error;
    }
  };

  /**
   * 설정 요약 확인 후 템플릿 평가 설정 저장
   * @param templateTypeCd 템플릿 타입 코드
   */
  const confirmEvalSetSummaryTemp = async (templateTypeCd: string) => {
    try {
      return await createEvalCycleAndSaveTemplateEvalSet(templateTypeCd);
    } catch (error) {
      console.error('설정 요약 확인 후 템플릿 저장 실패:', error);
      throw error;
    }
  };

  /**
   * 설정 요약 모달에서「적용」클릭 시 실제 저장 수행
   */
  const finalizeLoadApplyAfterSummaryView = async () => {
    const deferred = loadApplyDeferred;
    loadApplyDeferred = null;
    isPendingLoadApplySummary.value = false;
    if (!deferred) {
      return null;
    }
    try {
      let saveResponse: unknown = null;
      if (loadSettingsData.settingGbn === 'copy') {
        saveResponse = await finalizeEvalSetSummaryCopy();
      } else if (loadSettingsData.settingGbn === 'temp') {
        saveResponse = await createEvalCycleAndSaveTemplateEvalSet(loadSettingsData.tempGbn);
      }
      if (!saveResponse) {
        deferred.resolve(null);
        return null;
      }
      deferred.resolve(saveResponse);
      return saveResponse;
    } catch (error) {
      if (loadSettingsData.settingGbn === 'copy' && loadSettingsData.copyToNewEvalCycle) {
        await cleanupPendingCopyEvalCycle();
      }
      deferred.reject(error);
      throw error;
    }
  };

  /**
   * 설정 요약 모달을 닫기만 한 경우(적용 없음) — 저장하지 않고 대기 중인 execTempSet Promise 종료
   */
  const cancelLoadApplySummaryView = async () => {
    const deferred = loadApplyDeferred;
    loadApplyDeferred = null;
    isPendingLoadApplySummary.value = false;
    if (loadSettingsData.settingGbn === 'copy' && loadSettingsData.copyToNewEvalCycle) {
      await cleanupPendingCopyEvalCycle();
    }
    deferred?.resolve(null);
  };

  /**
   * 템플릿 설정 실행
   * 목적: 선택한 옵션에 따라 복사 또는 템플릿 설정 실행
   */
  const execTempSet = async () => {
    try {
      let prepared: unknown = null;
      if (loadSettingsData.settingGbn === 'copy') {
        prepared = await prepareEvalSetSummaryCopy();
      } else if (loadSettingsData.settingGbn === 'temp') {
        return await createEvalCycleAndSaveTemplateEvalSet(loadSettingsData.tempGbn);
      }

      if (!prepared) {
        return null;
      }

      return await new Promise((resolve, reject) => {
        loadApplyDeferred = { resolve, reject };
        isPendingLoadApplySummary.value = true;
      });
    } catch (error) {
      loadApplyDeferred = null;
      isPendingLoadApplySummary.value = false;
      if (loadSettingsData.settingGbn === 'copy' && loadSettingsData.copyToNewEvalCycle) {
        await cleanupPendingCopyEvalCycle();
      }
      console.error('템플릿 설정 실행 실패:', error);
      throw error;
    }
  };

  /**
   * 인사평가 Wizard 모달 열기
   */
  const openInsaWizard = () => {
    wizardEntryMode.value = 'new';
    wizardNoManagerDeptCount.value = 0;
    wizardStep.value = 'main';
    wizardEvalCycleCreated.value = false;
    wizardEvalTargetCreated.value = false;
    wizardGrpUserAssigned.value = false;
    wizardEvalCycleNm.value = settingsT('wizard.defaultEvalCycleNm');
    wizardData.performExecYn = false;
    wizardData.abilityExecYn = false;
    evalBaseData.abilityResultViewList = [];
    isInsaWizardOpen.value = true;
  };

  const isWizardYn = (value: unknown) => value === true || String(value ?? '').trim() === 'Y';

  const countDeptsWithoutChief = () => {
    const list = evalTargetSelectionData.insaDeptList || [];
    return list.filter((row: Record<string, unknown>) => {
      const chiefId = String(row.chiefUserId ?? row.chiefId ?? '').trim();
      const chiefNm = String(row.chiefNm ?? row.userNm ?? row.chiefUserNm ?? '').trim();
      return !chiefId && !chiefNm;
    }).length;
  };

  /**
   * 템플릿 적용 직후: cycle 단계 Wizard 오픈 (레거시 evalSetTemp 후 Wizard)
   */
  const openInsaWizardAfterTemplateApply = async (evalCycleCd: string, templateTypeCd: string) => {
    wizardEntryMode.value = 'template';
    wizardStep.value = 'cycle';
    wizardEvalCycleCreated.value = true;
    wizardEvalTargetCreated.value = false;
    wizardGrpUserAssigned.value = false;

    const templateDef = getEvalSettingsTemplateDef(templateTypeCd);
    const cycleLabel = evalGbnDataList.value.find(g => String(g.id) === String(evalCycleCd))?.label;
    wizardEvalCycleNm.value = String(cycleLabel || templateDef?.evalCycleNm || settingsT('wizard.defaultEvalCycleNm'));

    wizardData.performExecYn = isWizardYn(evalBaseData.baseData.performExecYn);
    wizardData.abilityExecYn = isWizardYn(evalBaseData.baseData.abilityExecYn);

    try {
      await loadInsaDeptList();
      wizardNoManagerDeptCount.value = countDeptsWithoutChief();
    } catch {
      wizardNoManagerDeptCount.value = 0;
    }

    isInsaWizardOpen.value = true;
  };

  const requestDeptManagerModalFromWizard = () => {
    isDeptManagerModalRequested.value = true;
  };

  /**
   * Wizard 단계 변경
   */
  const setWizardStep = (
    step:
      | 'main'
      | 'cycle'
      | 'perform'
      | 'ability'
      | 'level'
      | 'multiphase'
      | 'period'
      | 'capa'
      | 'grp'
      | 'perfmdl'
      | 'insa'
      | 'result',
  ) => {
    wizardStep.value = step;
    // 신규 마법사(cycle)에서도 부서장 미지정 조직 안내 배너 표시용 건수 조회
    if (step === 'cycle') {
      void (async () => {
        try {
          await loadInsaDeptList();
          wizardNoManagerDeptCount.value = countDeptsWithoutChief();
        } catch {
          wizardNoManagerDeptCount.value = 0;
        }
      })();
    }
    if (step === 'perform' || step === 'ability') {
      ensureEvalMethodCdDefaults();
    }
    if (step === 'level') {
      if (!evalBaseData.baseData.seniorEvalCd) {
        evalBaseData.baseData.seniorEvalCd = 'fir';
      }
    }
    if (step === 'capa') {
      const grpList = evalBaseData.abilityCmptGrpList as { value?: string; cmptGrpId?: string }[];
      if (!evalBaseData.baseData.abilityCmptGrpId && grpList?.length > 0) {
        const first = grpList[0];
        evalBaseData.baseData.abilityCmptGrpId = first?.value ?? first?.cmptGrpId ?? '';
      }
    }
    if (step === 'insa') {
      // 멀티셀렉트 UI에서 사용할 배열 필드 보정 (기존 단일 값과의 호환)
      const list = (evalData as { evalGrpList: any[] }).evalGrpList || [];
      list.forEach((g: any) => {
        if (!Array.isArray(g.posIds)) g.posIds = g.posId ? [g.posId] : [];
        if (!Array.isArray(g.jikgubIds)) g.jikgubIds = g.jikgubId ? [g.jikgubId] : [];
        if (!Array.isArray(g.jobIds)) g.jobIds = g.jobId ? [g.jobId] : [];
        if (!Array.isArray(g.deptIds)) g.deptIds = g.deptId ? [g.deptId] : [];
        if (!Array.isArray(g.userIds)) g.userIds = g.userId ? [g.userId] : [];

        g.posIds = (g.posIds || []).filter((v: any) => !!v);
        g.jikgubIds = (g.jikgubIds || []).filter((v: any) => !!v);
        g.jobIds = (g.jobIds || []).filter((v: any) => !!v);
        g.deptIds = (g.deptIds || []).filter((v: any) => !!v);
        g.userIds = (g.userIds || []).filter((v: any) => !!v);
      });

      void initializeEvalTargetSelectionData().catch((e) => {
        console.error(e);
      });
    }
    if (step === 'result') {
      // 원본 Step9는 result 단계에서 평가결과 조회기간 datepicker를 세팅했지만,
      // Nuxt UI에서는 UiDatePicker가 내부적으로 처리한다.
      // 필요한 기본값만 보정한다.
      if (!evalBaseData.targetMngData.evalResultGrpYn) evalBaseData.targetMngData.evalResultGrpYn = 'N';
      if (!evalBaseData.targetMngData.evalGradeScoreViewYn) evalBaseData.targetMngData.evalGradeScoreViewYn = 'N';
      normalizeEvalResultScoreFormatFields(evalBaseData.targetMngData as Record<string, any>);
    }
    if (step === 'grp') {
      const list = (evalData as { evalGrpList: any[] }).evalGrpList;
      if (!Array.isArray(list) || list.length === 0) {
        const year = commonStore.findYear;
        const evalCycleCd = findEvalCycleCd.value || '';
        const base = evalBaseData.baseData;
        const createGroup = (name: string) => ({
          compId: '',
          year,
          evalCycleCd,
          evalGrpId: '',
          evalGrpNm: name,
          abilityEvalYn: !!base.abilityExecYn,
          processEvalYn: true,
          performEvalYn: !!base.performExecYn,
          resultEvalYn: true,
          processEvalMdlCd: '002',
          resultEvalMdlCd: '001',
          kpiAchieveYn: false,
          evalItemGrpId: '',
          posId: '',
          jikgubId: '',
          jobId: '',
          deptId: '',
          userId: '',
          posIds: [],
          jikgubIds: [],
          jobIds: [],
          deptIds: [],
          userIds: [],
        });
        (evalData as { evalGrpList: any[] }).evalGrpList = [createGroup(settingsT('m_960d34fc5a')), createGroup(settingsT('m_fae668c3a7'))];
      }
    }
  };

  /**
   * 평가군 추가 (Wizard/상세설정 공통 사용 가능)
   */
  const handleAddEvalGroup = () => {
    const year = commonStore.findYear;
    const evalCycleCd = findEvalCycleCd.value || '';
    const newGroup = {
      compId: '',
      year,
      evalCycleCd,
      evalGrpId: '',
      evalGrpNm: '',
      abilityEvalYn: false,
      processEvalYn: true,
      performEvalYn: false,
      resultEvalYn: true,
      processEvalMdlCd: '002',
      resultEvalMdlCd: '001',
      kpiAchieveYn: false,
      evalItemGrpId: '',
      posId: '',
      jikgubId: '',
      jobId: '',
      deptId: '',
      userId: '',
      posIds: [],
      jikgubIds: [],
      jobIds: [],
      deptIds: [],
      userIds: [],
    };
    (evalData as { evalGrpList: any[] }).evalGrpList.push(newGroup);
  };

  const countActiveEvalGrps = () => {
    const list = (evalData as { evalGrpList: any[] }).evalGrpList || [];
    return list.filter((g) => g?.delYn !== 'Y').length;
  };

  const isEvalGrpMarkedForDelete = (group: any) => group?.delYn === 'Y';

  /**
   * 평가군 삭제: 신규(미저장)는 목록에서 제거, 기존은 delYn·evalGrpDeleteList에 담아 저장 시 삭제
   */
  const handleRemoveEvalGroup = (index: number) => {
    const list = (evalData as { evalGrpList: any[] }).evalGrpList;
    const deleteList = (evalData as { evalGrpDeleteList: any[] }).evalGrpDeleteList;
    const group = list[index];
    if (!group) return;

    if (!group.evalGrpId) {
      list.splice(index, 1);
      return;
    }

    if (group.delYn === 'Y') return;

    group.delYn = 'Y';
    const id = String(group.evalGrpId);
    if (!deleteList.some((g) => String(g?.evalGrpId ?? '') === id)) {
      deleteList.push({ ...group });
    }
  };

  /** 기존 평가군 삭제 예약 취소 */
  const handleRestoreEvalGroup = (index: number) => {
    const list = (evalData as { evalGrpList: any[] }).evalGrpList;
    const deleteList = (evalData as { evalGrpDeleteList: any[] }).evalGrpDeleteList;
    const group = list[index];
    if (!group?.evalGrpId || group.delYn !== 'Y') return;

    group.delYn = 'N';
    const id = String(group.evalGrpId);
    const delIdx = deleteList.findIndex((g) => String(g?.evalGrpId ?? '') === id);
    if (delIdx >= 0) deleteList.splice(delIdx, 1);
  };

  /** Step01 평가기간(editList) — 성과/역량 중 하나라도 체크된 평가유형만 */
  const shouldIncludeEvalCardForEdit = (card: any, base: any): boolean => {
    const cd = card?.evalCd;
    if (cd === 'self') return !!(base.performSelfEvalYn || base.abilitySelfEvalYn);
    if (cd === 'fir') return !!(base.performExecYn || base.abilityExecYn);
    if (cd === 'sec') return base.seniorEvalCd === 'sec' || base.seniorEvalCd === 'thr';
    if (cd === 'thr') return base.seniorEvalCd === 'thr';
    if (cd === 'mate') return !!(base.performMateEvalYn || base.abilityMateEvalYn);
    if (cd === 'jnr') return !!(base.performSbrdEvalYn || base.abilitySbrdEvalYn);
    return false;
  };

  /** Step01 성과평가 체크된 평가유형만 evalCdList(perform)에 포함 */
  const shouldIncludePerformEvalCd = (card: any, base: any): boolean => {
    const cd = card?.evalCd;
    if (cd === 'self') return !!base.performSelfEvalYn;
    if (cd === 'fir') return true;
    if (cd === 'sec') return base.seniorEvalCd === 'sec' || base.seniorEvalCd === 'thr';
    if (cd === 'thr') return base.seniorEvalCd === 'thr';
    if (cd === 'mate') return !!base.performMateEvalYn;
    if (cd === 'jnr') return !!base.performSbrdEvalYn;
    return false;
  };

  /** Step01 역량평가 체크된 평가유형만 evalCdList(ability)에 포함 */
  const shouldIncludeAbilityEvalCd = (card: any, base: any): boolean => {
    const cd = card?.evalCd;
    if (cd === 'self') return !!base.abilitySelfEvalYn;
    if (cd === 'fir') return true;
    if (cd === 'sec') return base.seniorEvalCd === 'sec' || base.seniorEvalCd === 'thr';
    if (cd === 'thr') return base.seniorEvalCd === 'thr';
    if (cd === 'mate') return !!base.abilityMateEvalYn;
    if (cd === 'jnr') return !!base.abilitySbrdEvalYn;
    return false;
  };

  /** 역량평가 시행 시 4-2 역량항목 체크 대상 평가유형 카드 여부 */
  const shouldValidateAbilityCapaForCard = (card: any, base: any): boolean => {
    if (!base.abilityExecYn) return false;
    return shouldIncludeAbilityEvalCd(card, base);
  };

  /** 평가설정 저장용 FormData 생성 (JSP saveEvalSetData 로직 반영) */
  const buildEvalSetFormData = (): FormData => {
    const formData = new FormData();
    const nvl = (v: any, d: string) => (v === undefined || v === null || v === '') ? d : String(v);
    const yn = (v: boolean) => (v ? 'Y' : 'N');
    const base = evalBaseData.baseData as any;
    const unit = evalBaseData.unitData as any;
    const year = commonStore.findYear || '';
    const evalCycleCd = findEvalCycleCd.value || '';

    formData.append('evalDegreeYn', base.evalDegreeYn ? 'Y' : 'N');
    if (base.evalDegreeYn) {
      formData.append('performEvalCycleCd', 'D');
      formData.append('abilityEvalCycleCd', 'D');
    } else {
      formData.append('performEvalCycleCd', nvl(base.performEvalCycleCd, ''));
      formData.append('abilityEvalCycleCd', nvl(base.abilityEvalCycleCd, ''));
    }
    formData.append('performEvalMethodCd', nvl(base.performEvalMethodCd, ''));
    formData.append('performSelfEvalYn', yn(!!base.performSelfEvalYn));
    formData.append('performMateEvalYn', yn(!!base.performMateEvalYn));
    formData.append('performSbrdEvalYn', yn(!!base.performSbrdEvalYn));
    formData.append('performRelativeYn', base.performEvalMethodCd === 'grade' ? nvl(base.performRelativeYn, 'N') : 'N');
    formData.append('abilityEvalMethodCd', nvl(base.abilityEvalMethodCd, ''));
    formData.append('abilitySelfEvalYn', yn(!!base.abilitySelfEvalYn));
    formData.append('abilityMateEvalYn', yn(!!base.abilityMateEvalYn));
    formData.append('abilitySbrdEvalYn', yn(!!base.abilitySbrdEvalYn));
    formData.append('performFeedbackWrtngYn', yn(!!base.performFeedbackWrtngYn));
    formData.append('abilityFeedbackWrtngYn', yn(!!base.abilityFeedbackWrtngYn));
    formData.append('performEvalFeedbackRequiredYn', yn(!!base.performEvalFeedbackRequiredYn));
    formData.append('abilityEvalFeedbackRequiredYn', yn(!!base.abilityEvalFeedbackRequiredYn));

    formData.append('seniorEvalCd', nvl(base.seniorEvalCd, ''));
    let seniorEvalMethodCd = nvl(base.seniorEvalMethodCd, '');
    if (base.seniorEvalCd === 'fir') seniorEvalMethodCd = '001';
    formData.append('seniorEvalMethodCd', seniorEvalMethodCd);
    formData.append('performEvalUnitCd1', nvl(unit.performEvalUnitCd1, ''));
    formData.append('performEvalUnitCd2', nvl(unit.performEvalUnitCd2, ''));
    if (seniorEvalMethodCd === '001' && base.performRelativeYn !== 'Y') {
      formData.append('evalItemGrpYn1', yn(!!unit.evalItemGrpYn1));
      formData.append('evalItemGrpYn2', yn(!!unit.evalItemGrpYn2));
    } else {
      formData.append('evalItemGrpYn1', '');
      formData.append('evalItemGrpYn2', '');
    }
    formData.append('abilityEvalUnitCd1', nvl(unit.abilityEvalUnitCd1, ''));
    formData.append('abilityEvalUnitCd2', nvl(unit.abilityEvalUnitCd2, ''));
    formData.append('mateEvalGiveupYn', nvl(base.mateEvalGiveupYn, 'N'));
    formData.append('jnrEvalGiveupYn', nvl(base.jnrEvalGiveupYn, 'N'));
    formData.append('secEvalGradeLimitCd', nvl(base.secEvalGradeLimitCd, ''));
    formData.append('thrEvalGradeLimitCd', nvl(base.thrEvalGradeLimitCd, ''));
    formData.append('performGradeScoreShowYn', yn(!!base.performGradeScoreShowYn));
    formData.append('abilityGradeScoreShowYn', yn(!!base.abilityGradeScoreShowYn));
    formData.append('abilityGradeScaleCalcYn', yn(!!base.abilityGradeScaleCalcYn));
    formData.append('abilityGradeScaleRate', base.abilityGradeScaleCalcYn ? nvl(base.abilityGradeScaleRate, '') : '');
    formData.append('perfGradeGrpLevelYn', yn(!!base.perfGradeGrpLevelYn));
    formData.append('cmptGradeGrpLevelYn', yn(!!base.cmptGradeGrpLevelYn));
    formData.append('cmptGrpId', nvl(base.abilityCmptGrpId, ''));
    formData.append('perfGradeGrpId', nvl(base.perfGradeGrpId, ''));
    formData.append('cmptGradeGrpId', nvl(base.cmptGradeGrpId, ''));
    formData.append('performMaxEvalScoreYn', yn(!!base.performMaxEvalScoreYn));
    formData.append('abilityMaxEvalScoreYn', yn(!!base.abilityMaxEvalScoreYn));
    let performMaxEvalScore = base.performMaxEvalScore;
    let abilityMaxEvalScore = base.abilityMaxEvalScore;
    if (base.performMaxEvalScoreYn && !performMaxEvalScore) performMaxEvalScore = '100';
    if (base.abilityMaxEvalScoreYn && !abilityMaxEvalScore) abilityMaxEvalScore = '100';
    formData.append('performMaxEvalScore', base.performMaxEvalScoreYn ? nvl(performMaxEvalScore, '') : '');
    formData.append('abilityMaxEvalScore', base.abilityMaxEvalScoreYn ? nvl(abilityMaxEvalScore, '') : '');

    const evalList = (evalData as { evalList: any[] }).evalList || [];
    let editIndex = 0;
    evalList
      .filter((card: any) => shouldIncludeEvalCardForEdit(card, base))
      .forEach((card: any) => {
      const capaGbnId = Array.isArray(card.capaGbnDatas) ? card.capaGbnDatas.join(',') : '';
      formData.append(`editList[${editIndex}].evalCd`, nvl(card.evalCd, ''));
      formData.append(`editList[${editIndex}].startDt`, nvl(card.startDt, ''));
      formData.append(`editList[${editIndex}].endDt`, nvl(card.endDt, ''));
      formData.append(`editList[${editIndex}].capaGbnId`, capaGbnId);
      editIndex++;
    });

    const grpList = (evalData as { evalGrpList: any[] }).evalGrpList || [];
    const unitData = evalBaseData.unitData as any;
    let newIdx = 0;
    let editGrpIdx = 0;
    let evalGrpNmEmptyCnt = 0;
    grpList.forEach((e: any) => {
      if (e.delYn === 'Y') return;
      const isEmptyGrpId = !e.evalGrpId;
      const performEvalYn = base.performExecYn && e.performEvalYn ? 'Y' : 'N';
      const abilityEvalYn = base.abilityExecYn && e.abilityEvalYn ? 'Y' : 'N';
      const resultEvalYn = base.performExecYn && e.resultEvalYn ? 'Y' : 'N';
      const processEvalYn = base.performExecYn && e.processEvalYn ? 'Y' : 'N';
      const resultEvalMdlCd = nvl(e.resultEvalMdlCd, '');
      const processEvalMdlCd = nvl(e.processEvalMdlCd, '');
      const hasBothItemGrp = !!(unitData.evalItemGrpId1 && unitData.evalItemGrpId2);
      const kpiAchieveYn = resultEvalMdlCd === '001' ? (hasBothItemGrp ? 'N' : (e.kpiAchieveYn ? 'Y' : 'N')) : 'N';
      const condEvalItemGrp = seniorEvalMethodCd === '001' && base.performRelativeYn !== 'Y' && (unitData.evalItemGrpYn1 || unitData.evalItemGrpYn2);
      const evalItemGrpId = condEvalItemGrp ? nvl(e.evalItemGrpId, '') : '';
      if (isEmptyGrpId) {
        formData.append(`evalGrpNewList[${newIdx}].evalGrpId`, '');
        formData.append(`evalGrpNewList[${newIdx}].evalGrpNm`, nvl(e.evalGrpNm, ''));
        formData.append(`evalGrpNewList[${newIdx}].performEvalYn`, performEvalYn);
        formData.append(`evalGrpNewList[${newIdx}].abilityEvalYn`, abilityEvalYn);
        formData.append(`evalGrpNewList[${newIdx}].resultEvalYn`, resultEvalYn);
        formData.append(`evalGrpNewList[${newIdx}].processEvalYn`, processEvalYn);
        formData.append(`evalGrpNewList[${newIdx}].kpiAchieveYn`, kpiAchieveYn);
        formData.append(`evalGrpNewList[${newIdx}].resultEvalMdlCd`, resultEvalMdlCd);
        formData.append(`evalGrpNewList[${newIdx}].processEvalMdlCd`, processEvalMdlCd);
        formData.append(`evalGrpNewList[${newIdx}].evalItemGrpId`, evalItemGrpId);
        newIdx++;
      } else {
        formData.append(`evalGrpEditList[${editGrpIdx}].evalGrpId`, e.evalGrpId);
        formData.append(`evalGrpEditList[${editGrpIdx}].evalGrpNm`, nvl(e.evalGrpNm, ''));
        formData.append(`evalGrpEditList[${editGrpIdx}].performEvalYn`, performEvalYn);
        formData.append(`evalGrpEditList[${editGrpIdx}].abilityEvalYn`, abilityEvalYn);
        formData.append(`evalGrpEditList[${editGrpIdx}].resultEvalYn`, resultEvalYn);
        formData.append(`evalGrpEditList[${editGrpIdx}].processEvalYn`, processEvalYn);
        formData.append(`evalGrpEditList[${editGrpIdx}].kpiAchieveYn`, kpiAchieveYn);
        formData.append(`evalGrpEditList[${editGrpIdx}].resultEvalMdlCd`, resultEvalMdlCd);
        formData.append(`evalGrpEditList[${editGrpIdx}].processEvalMdlCd`, processEvalMdlCd);
        formData.append(`evalGrpEditList[${editGrpIdx}].evalItemGrpId`, evalItemGrpId);
        editGrpIdx++;
      }
      if (!String(e.evalGrpNm ?? '').trim()) evalGrpNmEmptyCnt++;
    });
    if (evalGrpNmEmptyCnt > 0) {
      throw new Error(settingsT('m_ea890c1b63'));
    }

    const grpDeleteList = (evalData as { evalGrpDeleteList: any[] }).evalGrpDeleteList || [];
    grpDeleteList.forEach((e: any, i: number) => {
      formData.append(`evalGrpDeleteList[${i}].evalGrpId`, nvl(e.evalGrpId, ''));
      formData.append(`evalGrpDeleteList[${i}].evalGrpNm`, nvl(e.evalGrpNm, ''));
    });

    let cdIndex = 0;
    if (base.performExecYn) {
      evalList.forEach((card: any) => {
        if (!shouldIncludePerformEvalCd(card, base)) return;
        formData.append(`evalCdList[${cdIndex}].evalCycleCd`, evalCycleCd);
        formData.append(`evalCdList[${cdIndex}].evalGbnCd`, 'perform');
        formData.append(`evalCdList[${cdIndex}].perfGradeGrpId`, nvl(card.perfGradeGrpId, ''));
        formData.append(`evalCdList[${cdIndex}].evalCd`, nvl(card.evalCd, ''));
        cdIndex++;
      });
    }
    if (base.abilityExecYn) {
      evalList.forEach((card: any) => {
        if (!shouldIncludeAbilityEvalCd(card, base)) return;
        formData.append(`evalCdList[${cdIndex}].evalCycleCd`, evalCycleCd);
        formData.append(`evalCdList[${cdIndex}].evalGbnCd`, 'ability');
        formData.append(`evalCdList[${cdIndex}].cmptGradeGrpId`, nvl(card.cmptGradeGrpId, ''));
        formData.append(`evalCdList[${cdIndex}].evalCd`, nvl(card.evalCd, ''));
        cdIndex++;
      });
    }

    formData.append('year', year);
    formData.append('performExecYn', yn(!!base.performExecYn));
    formData.append('abilityExecYn', yn(!!base.abilityExecYn));
    formData.append('evalCycleCd', evalCycleCd);
    formData.append('abilityBeforeEvalShowYn', unit.abilityBeforeEvalShowYn ? 'Y' : 'N');
    return formData;
  };

  /** 4-2 역량평가 항목설정 저장 전 검증 (역량항목그룹·역량항목 체크) */
  const validateAbilityCapaItemsBeforeSave = (): boolean => {
    const base = evalBaseData.baseData as any;
    if (!base.abilityExecYn) return true;

    if (!String(base.abilityCmptGrpId ?? '').trim()) {
      showAlert(settingsT('basisCommon.notice'), settingsT('m_7f0ba97110'), 'warning');
      return false;
    }

    const capaGbnList = (evalData as { capaGbnList: any[] }).capaGbnList || [];
    if (!capaGbnList.length) {
      showAlert(settingsT('basisCommon.notice'), settingsT('m_bc001fb631'), 'warning');
      return false;
    }

    const evalList = (evalData as { evalList: any[] }).evalList || [];
    const cardsToValidate = evalList.filter(card => shouldValidateAbilityCapaForCard(card, base));
    if (!cardsToValidate.length) return true;

    const cardWithoutCapa = cardsToValidate.find(card => {
      const datas = Array.isArray(card.capaGbnDatas) ? card.capaGbnDatas : [];
      return datas.length === 0;
    });

    if (cardWithoutCapa) {
      const evalNm = cardWithoutCapa.evalNm || settingsT('m_a1d39e5bf4');
      showAlert(settingsT('basisCommon.notice'), settingsT('m_78d86e0d96', { evalNm }), 'warning');
      return false;
    }

    return true;
  };

  /** 평가군 목록 저장 전 검증 (평가군명·성과/역량 시행 항목) */
  const validateEvalGrpListBeforeSave = (): boolean => {
    const base = evalBaseData.baseData as any;
    const grpList = ((evalData as { evalGrpList: any[] }).evalGrpList || []).filter(g => g?.delYn !== 'Y');

    for (const group of grpList) {
      const grpNm = String(group.evalGrpNm ?? '').trim();
      if (!grpNm) {
        showAlert(settingsT('basisCommon.notice'), settingsT('m_ea890c1b63'), 'warning');
        return false;
      }

      const hasPerformItem = !!(group.resultEvalYn || group.processEvalYn);
      const hasAbilityItem = !!group.abilityEvalYn;

      if (base.performExecYn && !hasPerformItem) {
        if (!(base.abilityExecYn && hasAbilityItem)) {
          showAlert(settingsT('basisCommon.notice'), settingsT('m_c2920b4e36'), 'warning');
          return false;
        }
      }

      if (base.abilityExecYn && !hasAbilityItem) {
        if (!(base.performExecYn && hasPerformItem)) {
          showAlert(settingsT('basisCommon.notice'), settingsT('m_3c63e67b84'), 'warning');
          return false;
        }
      }
    }
    return true;
  };

  /** 부서장 직접 선정 ON 시 Step05 설정기간 필수 */
  const validateManagerTargetConfigBeforeSave = (): boolean => {
    const target = evalBaseData.targetMngData as any;
    if (target.managerTargetConfigYn !== 'Y') return true;

    const startDt = String(target.startDt ?? '').trim();
    const endDt = String(target.endDt ?? '').trim();
    if (!startDt || !endDt) {
      showAlert(settingsT('basisCommon.notice'), settingsT('m_3866c75c01'), 'warning');
      return false;
    }

    const start = new Date(String(startDt).replace(/\./g, '-'));
    const end = new Date(String(endDt).replace(/\./g, '-'));
    if (Number.isFinite(start.getTime()) && Number.isFinite(end.getTime()) && start > end) {
      showAlert(settingsT('basisCommon.notice'), settingsT('m_a5dbaf0d2c'), 'warning');
      return false;
    }
    return true;
  };

  /** 평가 설정 저장 전 통합 검증 (4-1 평가군 + 4-2 역량항목 + Step05 부서장 설정기간)
   * 각 스텝별 vaild 체크
   * */
  const validateEvalSettingsBeforeSave = (): boolean => {
    if(findCurrentStep.value === 'step04'){
      // step4
      // 평가군명
      if(!validateEvalGrpListBeforeSave()){
        return false;
      }
      //역량항목
      if(!validateAbilityCapaItemsBeforeSave()){
        return false;
      }
    }else if(findCurrentStep.value === 'step05' && !validateManagerTargetConfigBeforeSave()){
      // step5
      return false;
    }
    return true;
  };

  /** Step06/09 대상자/결과공개 설정 저장 파라미터 생성 (JSP saveTargetMngData 반영) */
  const buildTargetMngSaveParams = (): URLSearchParams => {
    const nvl = (v: any, d: string) => (v === undefined || v === null || v === '' ? d : String(v));
    const year = commonStore.findYear || '';
    const evalCycleCd = findEvalCycleCd.value || '';
    const target = evalBaseData.targetMngData as any;
    const managerTargetOn = target.managerTargetConfigYn === 'Y';

    const params = new URLSearchParams();
    params.append('year', year);
    params.append('startDt', managerTargetOn ? nvl(target.startDt, '') : '');
    params.append('endDt', managerTargetOn ? nvl(target.endDt, '') : '');
    params.append('guideContents', managerTargetOn ? nvl(target.guideContents, '') : '');
    params.append('evalGuideContents', nvl(target.evalGuideContents, ''));
    params.append('evalGuideContents2', nvl(target.evalGuideContents2, ''));
    params.append('viewStartDt', nvl(target.viewStartDt, ''));
    params.append('viewEndDt', nvl(target.viewEndDt, ''));
    params.append('seniorFeedbackViewYn', nvl(target.seniorFeedbackViewYn, 'N'));
    params.append('mateJnrFeedbackViewYn', nvl(target.mateJnrFeedbackViewYn, 'N'));
    params.append('evalCycleCd', evalCycleCd);
    params.append('evalResultGrpYn', nvl(target.evalResultGrpYn, 'N'));
    params.append('searchGrpYn', nvl(target.searchGrpYn, 'N'));
    params.append('evalGradeScoreViewYn', nvl(target.evalGradeScoreViewYn, 'N'));
    params.append('performResultProcessYn', nvl(target.performResultProcessYn, 'N'));
    params.append('evalDetailPopYn', nvl(target.evalDetailPopYn, 'N'));
    params.append('evalScoreSimuYn', nvl(target.evalScoreSimuYn, 'N'));
    params.append('evalCapaWeightYn', nvl(target.evalCapaWeightYn, 'N'));
    params.append('evalAvgScoreViewYn', nvl(target.evalAvgScoreViewYn, 'N'));
    params.append('evalResultSimuYn', nvl(target.evalResultSimuYn, 'N'));
    params.append('evalTargetListExpandYn', nvl(target.evalTargetListExpandYn, 'N'));
    params.append('evalTargetUserIdShowYn', nvl(target.evalTargetUserIdShowYn, 'N'));
    params.append('abilityGbnViewYn', nvl(target.abilityGbnViewYn, 'N'));
    params.append('abilitySelfScoreViewYn', nvl(target.abilitySelfScoreViewYn, 'N'));
    params.append('abilityTotAvgScoreViewYn', nvl(target.abilityTotAvgScoreViewYn, 'N'));
    params.append('abilityGrpAvgScoreViewYn', nvl(target.abilityGrpAvgScoreViewYn, 'N'));
    params.append('abilityChartScoreViewYn', nvl(target.abilityChartScoreViewYn, 'N'));
    params.append('gradeShowYn', evalBaseData.abilityResultViewList.includes('grade') ? 'Y' : 'N');
    params.append('scoreShowYn', evalBaseData.abilityResultViewList.includes('score') ? 'Y' : 'N');
    params.append('abilityResultViewGbn', target.abilityGbnViewYn === 'Y' ? nvl(evalBaseData.abilityResultViewGbn, '') : '');
    params.append('abilityResultViewItem', nvl(evalBaseData.abilityResultViewItem, ''));
    params.append('managerTargetConfigYn', nvl(target.managerTargetConfigYn, 'N'));
    normalizeEvalResultScoreFormatFields(target);
    params.append('evalResultScoreScale', String(target.evalResultScoreScale ?? 2));
    params.append('evalResultRoundGbn', nvl(target.evalResultRoundGbn, 'round'));
    return params;
  };

  /**
   * 평가설정 저장 (기본/단위/평가군/평가레벨 등)
   * @param options.skipConfirm true면 확인 없이 바로 저장
   */
  const resolveGradeSettingConflictBeforeSave = async (): Promise<boolean> => {
    const base = evalBaseData.baseData;
    const ctx = buildEvalGradeSettingConflictContext(base);
    if (!hasAnyGradeLevelConflict(ctx)) {
      return true;
    }

    const keepAdjust = await confirmAsync(
      settingsT('m_314f02492e'),
      settingsT('m_8f2e666e23'),
    );

    if (keepAdjust) {
      resolveGradeConflictPreferAdjust(base, evalData.evalList);
    } else {
      resolveGradeConflictPreferLevelYn(base);
    }
    return true;
  };

  const handleSaveEvalSetData = async (options?: { skipConfirm?: boolean }): Promise<boolean> => {
    const year = commonStore.findYear;
    const evalCycleCd = findEvalCycleCd.value;
    if (!year || !evalCycleCd) {
      showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_d1da5a4a1a'), false, null, null, false);
      return false;
    }
    const doSave = async (): Promise<boolean> => {
      const conflictResolved = await resolveGradeSettingConflictBeforeSave();
      if (!conflictResolved) {
        return false;
      }

      if (!validateEvalSettingsBeforeSave()) {
        return false;
      }

      isSavingEvalSetData.value = true;
      isLoading.value = true;
      pauseEvalSettingsDirtyWatch();
      try {
        const formData = buildEvalSetFormData();
        const res = await settingsApi.saveEvalSetData(formData) as any;
        if (res?.result === 'FAIL') {
          const serverMsg = res?.msg ?? settingsT('basisCommon.saveFailedDefault');
          showAlert(settingsT('m_5f3ec4437a'), serverMsg);
          return false;
        }
        const targetParams = buildTargetMngSaveParams();
        const targetSaveRes = await settingsApi.insertTargetMngData(targetParams) as any;
        if (targetSaveRes?.result === 'FAIL') {
          const targetFailMsg = targetSaveRes?.msg ?? settingsT('m_12556e7240');
          showAlert(settingsT('m_5f3ec4437a'), targetFailMsg);
          return false;
        }
        setTargetMngData(targetSaveRes);
        if (res?.evalList) evalData.evalList = res.evalList;
        if (res?.evalGrpList) {
          const mapped = mapEvalGrpListFromServer(res.evalGrpList);
          (evalData as { evalGrpList: any[] }).evalGrpList = JSON.parse(JSON.stringify(mapped));
          syncEvalGrpListSaved(mapped);
        }
        (evalData as { evalGrpDeleteList: any[] }).evalGrpDeleteList = [];
        await nextTick();
        resetEvalSettingsDirty();
        showAlert(settingsT('targets.done'), settingsT('m_0c47ff0995'));
        await reloadSettings();
        return true;
      } catch (e: any) {
        console.error(e);
        const msg = e?.message || settingsT('basisCommon.genericSaveError');
        showAlert(settingsT('basisCommon.notice'), msg, 'warning');
        return false;
      } finally {
        isSavingEvalSetData.value = false;
        isLoading.value = false;
        resumeEvalSettingsDirtyWatch();
      }
    };
    if (options?.skipConfirm) {
      return await doSave();
    }
    return await new Promise((resolve) => {
      showConfirmDialog(settingsT('basisCommon.confirm'), settingsT('basisCommon.saveConfirm'), async () => {
        const ok = await doSave();
        resolve(ok);
      });
    });
  };

  /**
   * Wizard에서 신규 평가차수 저장 (이름 지정)
   */
  const saveWizardEvalCycle = async (evalCycleNm: string) => {
    const year = commonStore.findYear;
    if (!year) {
      showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_d84a65db8d'), false, null, null, false);
      return null;
    }
    const params = new URLSearchParams();
    params.append('findYear', year);
    params.append('year', year);
    params.append('evalCycleNm', evalCycleNm || settingsT('wizard.defaultEvalCycleNm'));
    const response = await settingsApi.saveEvalCycle(params);
    if (response?.evalCycleList?.length) {
      evalGbnDataList.value = response.evalCycleList.map((item: { evalCycleCd: string; evalCycleNm: string }) => ({
        id: item.evalCycleCd,
        label: item.evalCycleNm,
      }));
      const newEvalCycleCd = response.evalCycleCd ?? response.evalCycleList[response.evalCycleList.length - 1]?.evalCycleCd;
      if (newEvalCycleCd) {
        findEvalCycleCd.value = newEvalCycleCd;
        // 신규 평가차수 생성/전환 시 대상자 옵션 데이터는 재로딩 필요
        evalTargetSelectionData.isInitialized = false;
        await switchEvalCycle(newEvalCycleCd);
      }
      return response;
    }
    return null;
  };

  /**
   * Wizard cycle 단계에서 평가차수명 변경분을 서버에 반영 (템플릿 적용 후 등 이미 차수가 있는 경우)
   */
  const persistWizardEvalCycleNm = async () => {
    const evalCycleCd = findEvalCycleCd.value;
    const evalCycleNm = (wizardEvalCycleNm.value || '').trim();
    if (!evalCycleCd) {
      showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_9614a76811'), false, null, null, false);
      return false;
    }
    if (!evalCycleNm) {
      showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_04e6133fb1'), false, null, null, false);
      return false;
    }

    const tab = evalGbnDataList.value.find((item: { id: string }) => item.id === evalCycleCd) as
      | { id: string; label: string }
      | undefined;
    if (tab?.label === evalCycleNm) {
      wizardEvalCycleNm.value = evalCycleNm;
      return true;
    }

    const params = new URLSearchParams();
    params.append('year', commonStore.findYear);
    params.append('findYear', commonStore.findYear);
    params.append('evalCycleCd', evalCycleCd);
    params.append('evalCycleNm', evalCycleNm);

    await settingsApi.updateEvalCycleName(params);
    if (tab) {
      tab.label = evalCycleNm;
    }
    wizardEvalCycleNm.value = evalCycleNm;
    return true;
  };

  /**
   * Wizard cycle 단계에서 settingsT('common.paging.next') 클릭: 신규 차수 생성 후 perform/ability 단계로
   */
  const goWizardCycleNext = async () => {
    if (!wizardData.performExecYn && !wizardData.abilityExecYn) {
      showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_ad23affbee'), false, null, null, false);
      return;
    }
    if (!wizardEvalCycleCreated.value) {
      showConfirmDialog(settingsT('basisCommon.confirm'), settingsT('m_b47a7a28e3'), async () => {
        try {
          const res = await saveWizardEvalCycle(wizardEvalCycleNm.value?.trim() || settingsT('wizard.defaultEvalCycleNm'));
          if (!res || !findEvalCycleCd.value) {
            showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_b5ec780124'), false, null, null, false);
            return;
          }

          wizardEvalCycleCreated.value = true;
          evalBaseData.baseData.performExecYn = wizardData.performExecYn;
          evalBaseData.baseData.abilityExecYn = wizardData.abilityExecYn;
          wizardStep.value = wizardData.performExecYn ? 'perform' : 'ability';
          ensureEvalMethodCdDefaults();
        } catch (e) {
          console.error(e);
        }
      });
    } else {
      try {
        // 템플릿 적용 등 이미 차수가 있는 경우 — 입력한 평가차수명으로 갱신
        const ok = await persistWizardEvalCycleNm();
        if (!ok) return;

        evalBaseData.baseData.performExecYn = wizardData.performExecYn;
        evalBaseData.baseData.abilityExecYn = wizardData.abilityExecYn;
        wizardStep.value = wizardData.performExecYn ? 'perform' : 'ability';
        ensureEvalMethodCdDefaults();
      } catch (e) {
        showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_f3563d8045'), false, null, null, false);
      }
    }
  };

  /**
   * Wizard perform 단계에서 settingsT('common.paging.next'): 검증 후 ability 또는 level로
   */
  const goWizardPerformNext = () => {
    ensureEvalMethodCdDefaults();
    if (
      evalBaseData.baseData.performEvalMethodCd !== 'grade' &&
      evalBaseData.baseData.performEvalMethodCd !== 'score'
    ) {
      showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_546c085537'), false, null, null, false);
      return;
    }
    if (wizardData.abilityExecYn) {
      wizardStep.value = 'ability';
    } else {
      wizardStep.value = 'level';
    }
  };

  /**
   * Wizard ability 단계에서 settingsT('common.paging.next'): 검증 후 level로
   */
  const goWizardAbilityNext = () => {
    ensureEvalMethodCdDefaults();
    if (
      evalBaseData.baseData.abilityEvalMethodCd !== 'grade' &&
      evalBaseData.baseData.abilityEvalMethodCd !== 'score'
    ) {
      showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_ea0d950c4f'), false, null, null, false);
      return;
    }
    wizardStep.value = 'level';
  };

  /**
   * Wizard level 단계에서 settingsT('common.paging.next'): multiphase(다면평가)로
   */
  const goWizardLevelNext = () => {
    wizardStep.value = 'multiphase';
  };

  /**
   * Wizard multiphase 단계에서 settingsT('common.paging.next'): period(평가기간)로
   */
  const goWizardMultiphaseNext = () => {
    wizardStep.value = 'period';
  };

  /**
   * 선택한 역량항목그룹에 해당하는 역량 구분 목록(capaGbnList)을 API로 조회해 evalData.capaGbnList만 갱신한다.
   * evalList는 건드리지 않는다.
   * @param cmptGrpId 역량항목그룹 ID. 생략 시 evalBaseData.baseData.abilityCmptGrpId 사용
   */
  const loadCapaGbnForGroup = async (cmptGrpId?: string) => {
    const grpId = cmptGrpId ?? evalBaseData.baseData.abilityCmptGrpId;
    if (!grpId || !commonStore.findYear || !findEvalCycleCd.value) return;
    const params = new URLSearchParams();
    params.append('findYear', commonStore.findYear);
    params.append('evalCycleCd', findEvalCycleCd.value);
    params.append('cmptGrpId', grpId);
    const res = await settingsApi.selectChgCapaGbnList(params);
    const list = res?.capaGbnList ?? res?.list;
    if (Array.isArray(list)) {
      pauseEvalSettingsDirtyWatch();
      try {
        (evalData as { capaGbnList: any[] }).capaGbnList = list;
        patchEvalDataSnapshot({ capaGbnList: list });
      } finally {
        resumeEvalSettingsDirtyWatch();
      }
    }
  };

  /**
   * Wizard period 단계에서 settingsT('common.paging.next'):
   * - 역량평가 선택 시: capa(역량평가 항목 그룹)로 이동 후 capaGbnList 로드
   * - 역량 미선택 시: capa 생략하고 grp(평가군 설정)로
   */
  const goWizardPeriodNext = async () => {
    if (wizardData.abilityExecYn) {
      setWizardStep('capa');
      if (evalBaseData.baseData.abilityCmptGrpId) {
        await loadCapaGbnForGroup(evalBaseData.baseData.abilityCmptGrpId);
      }
    } else {
      setWizardStep('grp');
    }
  };

  /**
   * Wizard capa 단계에서 settingsT('common.paging.next'): grp(평가군 설정)으로
   */
  const goWizardCapaNext = () => {
    if (!validateAbilityCapaItemsBeforeSave()) {
      return;
    }
    setWizardStep('grp');
  };

  /**
   * Wizard insa 단계 진입 전, 서버 기준 평가군 목록 재조회 (JSP selectGrpUserBase 패턴)
   */
  const loadWizardInsaEvalGrpList = async () => {
    const year = commonStore.findYear;
    const evalCycleCd = findEvalCycleCd.value || '';
    if (!year || !evalCycleCd) return;

    const params = new URLSearchParams();
    params.append('findYear', year);
    params.append('evalCycleCd', evalCycleCd);

    const res = await settingsApi.selectEvalGrpList(params);
    const serverList: any[] = (res as any)?.evalGrpList ?? [];
    if (!Array.isArray(serverList) || serverList.length === 0) return;

    const beforeList = (evalData as { evalGrpList: any[] }).evalGrpList || [];
    const findPrev = (grpId: string, grpNm: string) => {
      return beforeList.find((g: any) => {
        if (grpId && g.evalGrpId && g.evalGrpId === grpId) return true;
        return !!grpNm && !!g.evalGrpNm && String(g.evalGrpNm) === String(grpNm);
      });
    };
    const toBoolYn = (val: any, fallback = false) => {
      if (val === undefined || val === null || val === '') return fallback;
      return val === true || String(val) === 'Y';
    };

    (evalData as { evalGrpList: any[] }).evalGrpList = serverList.map((row: any) => {
      const prev = findPrev(row.evalGrpId ?? '', row.evalGrpNm ?? '');
      const prevPerform = toBoolYn(prev?.performEvalYn, false);
      const prevAbility = toBoolYn(prev?.abilityEvalYn, false);
      const prevResult = toBoolYn(prev?.resultEvalYn, false);
      const prevProcess = toBoolYn(prev?.processEvalYn, false);
      return {
        ...row,
        performEvalYn: toBoolYn(row?.performEvalYn, prevPerform),
        abilityEvalYn: toBoolYn(row?.abilityEvalYn, prevAbility),
        resultEvalYn: toBoolYn(row?.resultEvalYn, prevResult),
        processEvalYn: toBoolYn(row?.processEvalYn, prevProcess),
        resultEvalMdlCd: row?.resultEvalMdlCd ?? prev?.resultEvalMdlCd ?? '',
        processEvalMdlCd: row?.processEvalMdlCd ?? prev?.processEvalMdlCd ?? '',
        targetCnt: row?.targetCnt ?? row?.evalTargetCnt ?? prev?.targetCnt ?? prev?.evalTargetCnt ?? 0,
        evalTargetCnt: row?.evalTargetCnt ?? row?.targetCnt ?? prev?.evalTargetCnt ?? prev?.targetCnt ?? 0,
        posId: prev?.posId ?? '',
        jikgubId: prev?.jikgubId ?? '',
        jobId: prev?.jobId ?? '',
        deptId: prev?.deptId ?? '',
        userId: prev?.userId ?? '',
        posIds: Array.isArray(prev?.posIds) ? prev.posIds : [],
        jikgubIds: Array.isArray(prev?.jikgubIds) ? prev.jikgubIds : [],
        jobIds: Array.isArray(prev?.jobIds) ? prev.jobIds : [],
        deptIds: Array.isArray(prev?.deptIds) ? prev.deptIds : [],
        userIds: Array.isArray(prev?.userIds) ? prev.userIds : [],
      };
    });
  };

  /**
   * Wizard insa 단계 진입 전, 평가대상자 생성 (JSP saveEvalSetData 내부 createInsaData 패턴)
   * - legacy: hisMon = 'now'로 1회 생성 후 평가군별 대상자 배정 진행
   */
  const ensureWizardEvalTargetCreated = async (): Promise<boolean> => {
    if (wizardEvalTargetCreated.value) return true;
    const year = commonStore.findYear;
    const evalCycleCd = findEvalCycleCd.value || '';
    if (!year || !evalCycleCd) {
      showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_4f7086c2e8'), false, null, null, false);
      return false;
    }

    try {
      const params = new URLSearchParams();
      params.append('findYear', year);
      params.append('year', year);
      params.append('evalCycleCd', evalCycleCd);
      params.append('hisMon', 'now');
      params.append('hisMonNm', settingsT('m_e7755cd501'));
      await settingsApi.insertInsaData(params);
      wizardEvalTargetCreated.value = true;
      return true;
    } catch (e) {
      console.error('평가대상자 생성 실패:', e);
      showAlert(settingsT('basisCommon.error'), settingsT('m_02a0a79107'));
      return false;
    }
  };

  /**
   * Wizard perfmdl(성과평가 모듈) 단계 표시 여부 — JSP getPerformExecYn && getPerformEvalYn 와 동일
   */
  const hasWizardPerformEvalGroup = () => {
    const list = (evalData as { evalGrpList: any[] }).evalGrpList || [];
    return list.some((g) => g?.delYn !== 'Y' && !!g?.performEvalYn);
  };

  const shouldShowWizardPerfmdlStep = () => {
    return !!evalBaseData.baseData.performExecYn && hasWizardPerformEvalGroup();
  };

  /**
   * Wizard insa(평가대상자 배정) 단계로 이동 — 저장 후 insa 데이터 로드
   */
  const goWizardInsaStep = async (): Promise<boolean> => {
    const saved = await handleSaveEvalSetData({ skipConfirm: true });
    if (!saved) return false;
    const targetCreated = await ensureWizardEvalTargetCreated();
    if (!targetCreated) return false;
    await loadWizardInsaEvalGrpList();
    setWizardStep('insa');
    return true;
  };

  /**
   * Wizard perfmdl 단계에서 settingsT('common.paging.next'): insa(평가대상자 배정 기준)로
   */
  const goWizardPerfMdlNext = async () => {
    await goWizardInsaStep();
  };

  /**
   * Wizard insa 단계에서 [이전] 클릭 시 이동할 단계
   */
  const getWizardInsaBackStep = (): 'perfmdl' | 'grp' => {
    return shouldShowWizardPerfmdlStep() ? 'perfmdl' : 'grp';
  };

  /**
   * Wizard grp 단계에서 settingsT('common.paging.next'):
   * - 성과평가 시행 + 성과평가군 있음 → perfmdl
   * - 그 외 → insa (perfmdl 생략, JSP goStepInsa 와 동일)
   */
  const goWizardGrpNext = async () => {
    if (!validateEvalSettingsBeforeSave()) {
      return;
    }
    if (shouldShowWizardPerfmdlStep()) {
      setWizardStep('perfmdl');
      return;
    }
    await goWizardInsaStep();
  };

  /**
   * Wizard insa 단계에서 settingsT('common.paging.next'): 결과공개 설정(result)으로
   */
  const goWizardInsaNext = async () => {
    if (!wizardGrpUserAssigned.value) {
      const confirmed = await confirmAsync(
        settingsT('basisCommon.confirm'),
        settingsT('wizard.skipGrpAssignConfirm'),
      );
      if (!confirmed) return;
    }
    setWizardStep('result');
  };

  /**
   * Wizard result 단계에서 settingsT('targets.done'): 저장 후 Wizard 종료 및 설정 요약 팝업
   */
  const goWizardResultNext = async () => {
    const saved = await handleSaveEvalSetData({ skipConfirm: true });
    if (!saved) return;
    isInsaWizardOpen.value = false;

    const openSummary = async () => {
      try {
        await openEvalSetSummary(commonStore.findYear, findEvalCycleCd.value || '');
        await nextTick();
        isSettingsSummaryModalOpen.value = true;
      } catch (e) {
        console.error('설정 요약 팝업 열기 실패:', e);
      }
    };

    if (wizardEntryMode.value === 'template') {
      showConfirm(
        'info',
        settingsT('basisCommon.notice'),
        settingsT('wizard.completeSummaryGuide'),
        false,
        () => {
          void openSummary();
        },
        null,
        false,
      );
      return;
    }

    await openSummary();
  };

  /**
   * 평가군별 대상자 배정 (createGrpUser API 호출)
   * @param createGbn 'button' = settingsT('m_0480f70a11') 버튼 클릭 시
   * @param deleteYn Step07 모달 등에서만 전달 (미전달 시 서버로 보내지 않음)
   */
  const initGrpUserAssignSelectedIds = () => {
    const list = (evalData as { evalGrpList: any[] }).evalGrpList || [];
    grpUserAssignSelectedIds.value = list.map((g: any) => g.evalGrpId).filter((id: any) => !!id).map((id: any) => String(id));
  };

  const toggleGrpUserAssignSelectedId = (evalGrpId: string, checked: boolean) => {
    if (!evalGrpId) return;
    const id = String(evalGrpId);
    if (checked) {
      if (!grpUserAssignSelectedIds.value.includes(id)) grpUserAssignSelectedIds.value.push(id);
    } else {
      grpUserAssignSelectedIds.value = grpUserAssignSelectedIds.value.filter(v => v !== id);
    }
  };

  const isGrpUserAssignSelected = (evalGrpId: string) => {
    if (!evalGrpId) return false;
    return grpUserAssignSelectedIds.value.includes(String(evalGrpId));
  };

  /**
   * createGrpUser 성공 메시지 (Vue Step 번호 기준 i18n, 서버 msg Step5/Step3 문구 우회)
   */
  const buildGrpUserAssignSuccessMessage = (serverGrpList: any[], fromWizard = false) => {
    const personUnit = settingsT('wizard.personUnit');
    let totCnt = 0;
    const groupLines: string[] = [];

    (serverGrpList || []).forEach((srv: any) => {
      const cnt = Number(srv?.targetCnt ?? srv?.TARGET_CNT ?? srv?.evalTargetCnt ?? 0);
      const nm = String(srv?.evalGrpNm ?? srv?.EVAL_GRP_NM ?? '');
      if (Number.isFinite(cnt)) totCnt += cnt;
      groupLines.push(`${nm} : ${Number.isFinite(cnt) ? cnt : 0}${personUnit}`);
    });

    if (groupLines.length === 0) {
      const localList = (evalData as { evalGrpList: any[] }).evalGrpList || [];
      localList.forEach((g: any) => {
        const cnt = Number(g?.targetCnt ?? g?.evalTargetCnt ?? 0);
        if (Number.isFinite(cnt)) totCnt += cnt;
        groupLines.push(`${g.evalGrpNm ?? ''} : ${Number.isFinite(cnt) ? cnt : 0}${personUnit}`);
      });
    }

    return [
      settingsT('wizard.grpUserAssignStatusTitle'),
      '<br>',
      `${settingsT('wizard.evalTargetTotCnt')} : ${totCnt}${personUnit}`,
      '<br>',
      ...groupLines.map((line) => `${line}<br>`),
      settingsT('wizard.grpUserAssignReassignHint'),
      '<br>',
      settingsT(fromWizard ? 'wizard.grpUserAssignAfterWizardHint' : 'wizard.grpUserAssignAfterAssignHint'),
    ].join('');
  };

  const createGrpUser = async (
    createGbn: 'button' = 'button',
    onSuccess?: () => void | Promise<void>,
    deleteYn?: string,
    fromWizard = false,
  ) => {
    const year = commonStore.findYear;
    const evalCycleCd = findEvalCycleCd.value || '';
    if (!year || !evalCycleCd) {
      showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_4f7086c2e8'), false, null, null, false);
      return;
    }
    const list = (evalData as { evalGrpList: any[] }).evalGrpList || [];
    const selectedIdSet = deleteYn ? new Set(grpUserAssignSelectedIds.value) : null;

    if (deleteYn && (!selectedIdSet || selectedIdSet.size === 0)) {
      showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_fbebd357df'), false, null, null, false);
      return;
    }

    // JSP와 동일하게 insa 진입 시 서버 평가군을 기준으로 하며, id가 없는 항목은 배정 대상에서 제외됨
    const formData = new FormData();
    formData.append('findYear', year);
    formData.append('year', year);
    formData.append('evalCycleCd', evalCycleCd);
    if (deleteYn) formData.append('deleteYn', deleteYn);

    const toCsv = (value: any) => {
      if (Array.isArray(value)) return value.filter((v) => !!v).join(',');
      return value ?? '';
    };

    let grpSetYn = false;
    let formIdx = 0;
    list.forEach((g: any) => {
      if (deleteYn && selectedIdSet && !selectedIdSet.has(String(g.evalGrpId ?? ''))) return;
      const posIds = toCsv(g.posIds ?? g.posId);
      const jikgubIds = toCsv(g.jikgubIds ?? g.jikgubId);
      const jobIds = toCsv(g.jobIds ?? g.jobId);
      const deptIds = toCsv(g.deptIds ?? g.deptId);
      const userIds = toCsv(g.userIds ?? g.userId);
      if (posIds || jikgubIds || jobIds || deptIds || userIds) {
        grpSetYn = true;
        formData.append(`evalGrpList[${formIdx}].evalGrpId`, g.evalGrpId ?? '');
        formData.append(`evalGrpList[${formIdx}].evalGrpNm`, g.evalGrpNm ?? '');
        formData.append(`evalGrpList[${formIdx}].findPosIds`, posIds);
        formData.append(`evalGrpList[${formIdx}].findJikgubIds`, jikgubIds);
        formData.append(`evalGrpList[${formIdx}].findJobIds`, jobIds);
        formData.append(`evalGrpList[${formIdx}].findDeptIds`, deptIds);
        formData.append(`evalGrpList[${formIdx}].findUserIds`, userIds);
        formIdx++;
      }
    });

    if (!grpSetYn) {
      showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_bc46c9d58b'), false, null, null, false);
      return;
    }

    showConfirmDialog(settingsT('basisCommon.confirm'), settingsT('wizard.grpUserAssignConfirm'), async () => {
      try {
        isLoading.value = true;
        const res = await settingsApi.createGrpUser(formData);
        const serverGrpList: any[] = (res as any)?.evalGrpList ?? [];
        if (Array.isArray(serverGrpList) && serverGrpList.length > 0) {
          const localList = (evalData as { evalGrpList: any[] }).evalGrpList || [];
          serverGrpList.forEach((srv: any) => {
            const id = srv?.evalGrpId ?? srv?.EVAL_GRP_ID;
            const local = localList.find((g: any) => String(g?.evalGrpId ?? '') === String(id ?? ''));
            if (!local) return;
            const cnt = srv?.targetCnt ?? srv?.TARGET_CNT ?? srv?.evalTargetCnt;
            if (cnt !== undefined && cnt !== null && cnt !== '') {
              const n = Number(cnt);
              if (Number.isFinite(n)) {
                local.targetCnt = n;
                local.evalTargetCnt = n;
              }
            }
          });
        }
        const msg = buildGrpUserAssignSuccessMessage(serverGrpList, fromWizard);
        wizardGrpUserAssigned.value = true;
        step07EvalGrpCountsReloadTick.value++;
        if (onSuccess) await onSuccess();
        showConfirm('success', settingsT('targets.done'), msg, false, null, null, false);
      } catch (e) {
        console.error('평가군별 대상자 배정 실패:', e);
      } finally {
        isLoading.value = false;
      }
    });
  };

  /**
   * 인사평가 Wizard 모달 닫기
   */
  const closeInsaWizard = () => {
    isInsaWizardOpen.value = false;
    wizardEntryMode.value = 'new';
    wizardNoManagerDeptCount.value = 0;
  };

  /**
   * X 버튼으로 Wizard를 닫을 때 단계별 종료 확인
   */
  const confirmInsaWizardClose = async (): Promise<boolean> => {
    const unsavedSettingSteps = ['perform', 'ability', 'level', 'multiphase', 'period', 'capa', 'grp', 'perfmdl'];
    const message = unsavedSettingSteps.includes(wizardStep.value)
      ? settingsT('m_c8fa2b2002')
      : settingsT('m_b3d9e261bf');

    return await confirmAsync(settingsT('basisCommon.confirm'), message);
  };

  /**
   * 불러오기 모달 열기 (Wizard 또는 상단 버튼에서 호출)
   * @param mode 'copy' 이전 설정 불러오기 | 'temp' 많이 쓰이는 평가설정 선택
   */
  const openLoadSettings = (mode: 'copy' | 'temp' = 'copy', copyToNewEvalCycle: boolean = false) => {
    loadSettingsData.settingGbn = mode;
    loadSettingsData.copyToNewEvalCycle = copyToNewEvalCycle;
    isLoadSettingsModalOpen.value = true;
  };

  /**
   * 상세설정 바로가기: 신규 평가차수 생성 후 해당 차수로 전환
   * @param evalCycleNm 생성할 차수명. 미전달 시 settingsT('wizard.defaultEvalCycleNm')
   */
  const addEvalCycle = async (evalCycleNm: string = settingsT('wizard.defaultEvalCycleNm')) => {
    try {
      const year = commonStore.findYear;
      if (!year) {
        showConfirm('warning', settingsT('basisCommon.notice'), settingsT('m_d84a65db8d'), false, null, null, false);
        return;
      }
      const params = new URLSearchParams();
      params.append('findYear', year);
      params.append('year', year);
      params.append('evalCycleNm', evalCycleNm || settingsT('wizard.defaultEvalCycleNm'));
      const response = await settingsApi.saveEvalCycle(params);
      if (response?.evalCycleList?.length) {
        evalGbnDataList.value = response.evalCycleList.map((item: { evalCycleCd: string; evalCycleNm: string }) => ({
          id: item.evalCycleCd,
          label: item.evalCycleNm,
        }));
        const newEvalCycleCd = response.evalCycleCd ?? response.evalCycleList[response.evalCycleList.length - 1]?.evalCycleCd;
        if (newEvalCycleCd) {
          findEvalCycleCd.value = newEvalCycleCd;
          await switchEvalCycle(newEvalCycleCd);
        }
      }
    } catch (error) {
      console.error('평가차수 추가 실패:', error);
      throw error;
    }
  };

  /**
   * Step07 평가군 선택 시 평가대상 현황 조회 (레거시 selectTargetStatusList_json.do)
   */
  const fetchStep07TargetStatusList = async (evalGrpId: string) => {
    const params = new URLSearchParams();
    params.append('findYear', commonStore.findYear);
    params.append('evalCycleCd', findEvalCycleCd.value || '');
    params.append('findUserId', '');
    if (evalGrpId) {
      params.append('evalGrpId', evalGrpId);
    }
    return await settingsApi.selectTargetStatusList(params);
  };

  /** Step06 진입 시 서버 기준 평가군 목록만 갱신 (Step04 미저장 편집은 evalGrpList에 유지) */
  const refreshEvalGrpListSavedFromServer = async () => {
    const year = commonStore.findYear;
    const evalCycleCd = findEvalCycleCd.value;
    if (!year || !evalCycleCd) {
      evalGrpListSaved.value = [];
      return;
    }
    try {
      const evalDataParams = new URLSearchParams();
      evalDataParams.append('findYear', year);
      evalDataParams.append('evalCycleCd', evalCycleCd);
      evalDataParams.append('cmptGrpId', '');
      const res = (await settingsApi.selectEvalData(evalDataParams)) as any;
      if (res?.evalGrpList) {
        syncEvalGrpListSaved(res.evalGrpList);
      } else {
        evalGrpListSaved.value = [];
      }
    } catch (e) {
      console.error(e);
    }
  };

  /* 설정 새로고침 */
  const reloadSettings = async () => {
    await switchEvalCycle(findEvalCycleCd.value);
  }

  /* step 이동 시 데이터 변경 사항 감지 */
  const stepDirtyWatch = async () => {
    return await new Promise((resolve) => {
      const hasUnsaved = hasUnsavedEvalSettings();

      //평가군 목록에서 기존 데이터중 평가군명 수정모드에서 값 없으면 원본 데이터 넣어줌
      //신규 추가했는데 평가군명 없으면 삭제함
      if(evalData.evalGrpList.length > 0){
        evalData.evalGrpList = evalData.evalGrpList
        // 1. evalGrpId와 evalGrpNm이 모두 없는 항목은 걸러냄 (삭제)
        .filter(grp => grp.evalGrpId || grp.evalGrpNm)
        // 2. 남은 항목 중 evalGrpId는 있으나 evalGrpNm이 없는 경우 원본 이름으로 복원
        .map(grp => {
          if (grp.evalGrpId && !grp.evalGrpNm) {
            grp.evalGrpNm = grp.oriEvalGrpNm;
          }
          return grp;
        });
      }

      if(!hasUnsaved){
        //변경 항목 없음
        resolve(true);
      }else{
        //변경 항목 있음
        showConfirmDialog(settingsT('basisCommon.confirm'), settingsT('m_a2d234ac33'),
          async () => {
            // 확인
            if(await handleSaveEvalSetData({ skipConfirm: true })){
              resolve(true);
            }else{
              resolve(false);
            };
          },
          async () => {
            //취소
            //다음 스텝에서의 변경 감지를  위해 현재 상태 snapshot 저장
            captureEvalSettingsSnapshot();
            resolve(true);
          },
          'warning',
          '',
          { confirmButtonText: settingsT('m_24218f15fc'), cancelButtonText: settingsT('m_36758693be')},
        );
      }
    });
  }

  return {
    findEvalCycleCd,
    evalGbnDataList,
    isEditMode,
    isSavingEvalSetData,
    isDetailedPopupSettingsOpen,
    isEvalSettingsDirty,
    hasUnsavedEvalSettings,
    stepSnapshots,
    isEvalSettingsDirtyWatchPaused,
    pauseEvalSettingsDirtyWatch,
    resumeEvalSettingsDirtyWatch,
    setEvalSettingsDirtyWatchPaused,
    isInsaWizardOpen,
    isLoadSettingsModalOpen,
    isSettingsSummaryModalOpen,
    isPendingLoadApplySummary,
    wizardStep,
    wizardEvalCycleNm,
    wizardData,
    wizardEvalCycleCreated,
    wizardEntryMode,
    wizardNoManagerDeptCount,
    isDeptManagerModalRequested,
    step07EvalGrpCountsReloadTick,
    grpUserAssignSelectedIds,
    evalBaseData,
    evalData,
    evalGrpListSaved,
    evalTargetSelectionData,
    evalSetData,
    loadSettingsData,
    selectedKrCycles,
    krCycleSelectOptions,

    // 평가차수 삭제 패널
    isDeletePanelOpen,
    deleteInput,
    currentEvalCycleLabel,
    isDeleteInputValid,
    findCurrentStep,

    //함수
    handleYearChange,
    handleEvalCycleChange,
    handleTabEdit,
    handleTabDelete,
    handleRowDragEnd,
    switchEvalCycle,
    ensureEvalMethodCdDefaults,
    createInsaSettingData,
    handleInitSelectedKrCycles,
    initializeEvalTargetSelectionData,
    loadEvalTargetSummaryData,
    searchUsersWithFilters,
    createInsaData,
    updateInsaData,
    popAddNewEmp,
    saveNewUserData,
    loadInsaDeptList,
    saveInsaDeptList,
    saveEditedUserData,
    deleteInsaUserData,
    openEvalSetSummary,
    onChangeQuickEdit,
    openLoadSettingsModal,
    getLoadSettingsEvalCycleList,
    copyEvalCycleSet,
    saveTemplateEvalSet,
    confirmEvalSetSummaryCopy,
    confirmEvalSetSummaryTemp,
    finalizeLoadApplyAfterSummaryView,
    cancelLoadApplySummaryView,
    execTempSet,
    openInsaWizard,
    openInsaWizardAfterTemplateApply,
    requestDeptManagerModalFromWizard,
    closeInsaWizard,
    confirmInsaWizardClose,
    openLoadSettings,
    addEvalCycle,
    setWizardStep,
    saveWizardEvalCycle,
    goWizardCycleNext,
    goWizardPerformNext,
    goWizardAbilityNext,
    goWizardLevelNext,
    goWizardMultiphaseNext,
    goWizardPeriodNext,
    goWizardCapaNext,
    goWizardGrpNext,
    goWizardPerfMdlNext,
    getWizardInsaBackStep,
    goWizardInsaNext,
    goWizardResultNext,
    createGrpUser,
    initGrpUserAssignSelectedIds,
    toggleGrpUserAssignSelectedId,
    isGrpUserAssignSelected,
    loadCapaGbnForGroup,
    handleAddEvalGroup,
    handleRemoveEvalGroup,
    handleRestoreEvalGroup,
    isEvalGrpMarkedForDelete,
    handleSaveEvalSetData,
    markEvalSettingsDirty,
    resetEvalSettingsDirty,
    fetchStep07TargetStatusList,
    refreshEvalGrpListSavedFromServer,
    toggleDetailedPopupSettings,
    toggleDeletePanel,
    closeDeletePanel,
    handleDeleteCurrentCycle,
    reloadSettings,
    stepDirtyWatch,
    isEvalResultScoreFormatVisible,
    evalResultScoreScaleOptions,
    evalResultRoundGbnOptions,
    evalResultScoreScaleModel,
    isEvalResultRoundGbnDisabled,
  };
};
